/**
 * Motor de booking (V69, Fase 1 — diseño aprobado 2026-07-04). Vista compartida:
 * Wellness usa kind=CLASS (tab "Clases"); Boutique usa kind=SLOT (sub-vista
 * "Reservas" dentro de Agenda). Mismo patrón que las vistas POS compartidas.
 *
 * Fase 1 = solo staff: calendario semanal de sesiones con cupo, panel por sesión
 * (cupos usados/libres + quién reservó + no-shows históricos por email), reservas
 * manuales con cupo atómico (el backend responde 409 si se llenó).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { api } from '../lib/api';
import { ContactPicker } from '../components/ContactPicker';
import { useContacts } from '../contacts/useContacts';
import { bookingLabels, verticalOfKind } from './bookingLabels';
import { cancellationPolicyText } from './cancellationPolicy';
import { useAuth } from '../auth/AuthProvider';
import {
  CANVAS_UNITS, LayoutChrome, SPOT_UNITS, SpotMapPicker, spotsCollide,
  type LayoutEdge, type RoomLayout, type SpotCell,
} from './SpotMapPicker';

export type Offering = {
  id: string; name: string; kind: 'CLASS' | 'SLOT'; serviceId: string | null;
  durationMinutes: number; defaultCapacity: number; color: string | null;
  sortOrder: number; isActive: boolean;
  // Fase 4 — servicio del catálogo ligado (Boutique): se hereda duración y el
  // precio se muestra desde el catálogo (no se copia).
  serviceName?: string | null; servicePrice?: string | number | null;
  serviceCurrency?: string | null;
  // V74 (Fase 5) — instructor/a asignado.
  instructorId?: string | null; instructorName?: string | null;
  // V88 — tipos de espacio asignados (lista no vacía = clase tipada).
  resourceTypes?: Array<{ id: string; name: string; capacity: number; isActive: boolean }>;
  // Epic Pagos (V101) — cobro en línea (Wellness).
  price?: string | number | null; paymentMode?: 'none' | 'required';
  noShowChargePct?: number; lateCancelWindowHours?: number;
  lateCancelChargePct?: number;
  // #578 (D2) — tope de cambios de horario por reserva (null = ilimitado).
  rescheduleLimit?: number | null;
  // C2 (#285) — sucursal de la clase/actividad.
  branchId?: string | null; branchName?: string | null;
};
// V88/V91 — AULA / espacio del tenant (recurso PERSISTENTE del admin: la
// capacidad y la disciplina viven en el aula hasta que él las reconfigure).
type ResourceType = {
  id: string; name: string; capacity: number; color: string | null;
  discipline?: string | null;
  sortOrder: number; isActive: boolean; offeringsCount?: number;
  // V93 — lugares activos del mapa (>0 = aula con mapa; capacity la define el mapa).
  spotsCount?: number;
  // C2 (#285) — sucursal del aula (null = compartida).
  branchId?: string | null; branchName?: string | null;
};
// C2 (#285) — sucursal del tenant (para los selectores de aula/clase).
type BranchOpt = { id: string; name: string; isActive: boolean; isDefault?: boolean };
// V93 — lugar del mapa (editor admin: solo se manipulan los activos; el
// backend desactiva/borra los que salen con guardrail de reservas futuras).
type RoomSpot = {
  id?: string; label: string; row: number; col: number;
  displayOrder?: number; isActive: boolean;
};
// V93 — lugar de una sesión con su estado (shape compartido con el público).
type SlotSpot = SpotCell;
// V89 — entrada de la sala de espera (vista staff).
type WaitlistEntry = {
  id: string; customerName: string; customerEmail: string; customerPhone: string | null;
  status: 'WAITING' | 'OFFERED' | 'CONVERTED' | 'EXPIRED' | 'DECLINED' | 'REMOVED';
  resourceTypeName: string | null; offeredAt: string | null; offerExpiresAt: string | null;
  createdAt: string;
  // V93 — lugar apartado por la oferta activa (aulas con mapa).
  spotName?: string | null;
};
export type Instructor = {
  id: string; name: string; bio: string | null; photoUrl: string | null; isActive: boolean;
};
type CatalogService = {
  id: string; name: string; defaultPrice: string | number; currency: string;
  durationMinutes: number;
};
const fmtPrice = (v: string | number | null | undefined, currency?: string | null) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}${currency ? ` ${currency}` : ''}` : null;
};
type SlotTypeOccupancy = {
  id: string; name: string; capacity: number; booked: number;
  /** V90 — lugares apartados por ofertas activas del waitlist. */
  held?: number;
  /** V93 — el aula tiene mapa de lugares (el staff debe asignar lugar). */
  hasMap?: boolean;
};
export type Slot = {
  id: string; offeringId: string; startsAt: string; durationMinutes: number;
  capacity: number; status: 'OPEN' | 'CANCELLED'; notes: string | null;
  offeringName: string; offeringKind: 'CLASS' | 'SLOT'; offeringColor: string | null;
  bookedCount: number;
  // V73 (fix D4): citas manuales vivas solapadas del mismo servicio — también
  // ocupan estación (solo SLOT ligado a service_catalog; CLASS siempre 0).
  appointmentsCount: number;
  // V74 (Fase 5): serie recurrente (badge) + instructor del offering.
  seriesId?: string | null; instructorName?: string | null;
  // V88 — ocupación por tipo (lista no vacía = sesión tipada; el cupo efectivo
  // es la SUMA de capacidades por tipo, no la columna capacity).
  resourceTypes?: SlotTypeOccupancy[];
  // V89 — gente activa en la sala de espera.
  waitlistCount?: number;
  // V90 — lugares apartados (ofertas activas): cuentan como ocupación.
  heldCount?: number;
  // V91 — instructor de la SESIÓN (null = hereda del offering).
  instructorId?: string | null;
};
// V90: el "lleno" incluye los lugares apartados (holds).
const occupiedOf = (s: Slot) =>
  s.resourceTypes?.length
    ? s.resourceTypes.reduce((a, t) => a + t.booked + (t.held ?? 0), 0)
    : s.bookedCount + (s.appointmentsCount ?? 0) + (s.heldCount ?? 0);
/** Cupo efectivo: suma por aula si la sesión tiene aulas; capacity si no. */
const capacityOf = (s: Slot) =>
  s.resourceTypes?.length
    ? s.resourceTypes.reduce((a, t) => a + t.capacity, 0)
    : s.capacity;
const typeSummary = (s: Slot) =>
  s.resourceTypes?.length
    ? s.resourceTypes.map((t) =>
        `${t.name} ${t.booked}/${t.capacity}${(t.held ?? 0) > 0 ? ` (+${t.held} apartado${t.held === 1 ? '' : 's'})` : ''}`,
      ).join(' · ')
    : '';
type Attendee = {
  id: string; customerName: string; customerEmail: string; customerPhone: string | null;
  status: 'BOOKED' | 'CANCELLED' | 'ATTENDED' | 'NO_SHOW'; source: 'PUBLIC' | 'ADMIN';
  contactId: string | null; createdAt: string; cancelledAt: string | null;
  cancelReason: string | null; noShowCount: number;
  // #578 — cancelación DENTRO de la ventana (badge "Cancelación tardía", como no-show).
  lateCancelled?: boolean;
  // Epic Pagos (V101) — estado de pago + monto (para refund/badge).
  paymentStatus?: 'none' | 'pending' | 'paid' | 'expired'; paidAmount?: string | number | null;
  // V75 (Fase 5): snapshot [{fieldId, label, value}] del formulario custom.
  customAnswers?: Array<{ fieldId: string; label: string; value: string }> | null;
  // V88 — snapshot del tipo de espacio elegido.
  resourceTypeName?: string | null;
  // V93 — aula + lugar de la reserva (para mover con re-asignación de lugar).
  resourceTypeId?: string | null;
  spotId?: string | null;
  spotName?: string | null;
};
type SlotDetail = {
  slotId: string; capacity: number; bookedCount: number; appointmentsCount: number;
  freeCount: number; attendees: Attendee[];
  // V88/V89/V90
  resourceTypes?: SlotTypeOccupancy[]; waitlistCount?: number; heldCount?: number;
};

const card = 'rounded-2xl border border-slate-200 bg-white';
const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';
const inputCls = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm';

const STATUS_META: Record<Attendee['status'], { label: string; cls: string }> = {
  BOOKED:    { label: 'Reservada', cls: 'bg-sky-100 text-sky-700' },
  ATTENDED:  { label: 'Asistió',   cls: 'bg-emerald-100 text-emerald-700' },
  NO_SHOW:   { label: 'No asistió', cls: 'bg-amber-100 text-amber-700' },
  CANCELLED: { label: 'Cancelada', cls: 'bg-slate-100 text-slate-500' },
};

const DAY_MS = 24 * 60 * 60 * 1000;
/** Lunes 00:00 local de la semana de `d`. */
function mondayOf(d: Date): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const dow = (x.getDay() + 6) % 7; // 0 = lunes
  return new Date(x.getTime() - dow * DAY_MS);
}
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
const fmtDayHead = (d: Date) =>
  d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' });
const fmtFull = (iso: string) =>
  new Date(iso).toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
const errMsg = (e: any, fallback: string) => e?.body?.message || e?.message || fallback;
const pad2 = (n: number) => String(n).padStart(2, '0');
// datetime-local en hora LOCAL (prellenar el editor de sesión).
const toDatetimeLocal = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const KIND_COPY = {
  CLASS: { offerings: 'Clases y actividades', newOffering: '+ Nueva clase', slot: 'sesión', empty: 'Sin sesiones esta semana. Crea la primera.' },
  SLOT:  { offerings: 'Tipos de cita', newOffering: '+ Nuevo tipo de cita', slot: 'horario', empty: 'Sin horarios esta semana. Crea el primero.' },
} as const;

export function BookingManagerView({
  kind, canWrite = true, canManage = true,
}: { kind: 'CLASS' | 'SLOT'; canWrite?: boolean; canManage?: boolean }) {
  const copy = KIND_COPY[kind];
  // #285 P2 — copy vertical-aware también en la agenda del staff (Boutique =
  // servicios / quién atiende).
  const L = bookingLabels(verticalOfKind(kind));
  // P4 (#285) — la sucursal ACTIVA de la barra superior está horneada en el JWT.
  // El backend (listSlots) ya filtra por ella; aquí solo la usamos como dep del
  // efecto para que la agenda (calendario Y lista) se recargue al cambiar de sede.
  const { user: bmUser } = useAuth();
  const bmActiveBranch = bmUser?.branch?.activeBranchId ?? '';
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showOfferings, setShowOfferings] = useState(false);
  const [showNewSlot, setShowNewSlot] = useState(false);
  const [detail, setDetail] = useState<Slot | null>(null);
  // Fase 5 (UX v2): vista lista/calendario también para el staff.
  const [view, setView] = useState<'calendario' | 'lista'>('calendario');
  // V74 (Fase 5, Task C): dropdowns de instructor/clase (filtro client-side).
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [showInstructors, setShowInstructors] = useState(false);
  const [instructorFilter, setInstructorFilter] = useState('');
  const [offeringFilter, setOfferingFilter] = useState('');
  // V88 — tipos de espacio (solo Wellness/CLASS).
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [showResourceTypes, setShowResourceTypes] = useState(false);

  const weekEnd = useMemo(() => new Date(weekStart.getTime() + 7 * DAY_MS), [weekStart]);

  const loadSlots = () =>
    api<Slot[]>(`/booking/slots?from=${weekStart.toISOString()}&to=${weekEnd.toISOString()}&kind=${kind}`)
      .then(setSlots)
      .catch((e: any) => setErr(errMsg(e, 'No se pudieron cargar las sesiones')));
  const loadOfferings = () =>
    api<Offering[]>(`/booking/offerings?kind=${kind}`)
      .then(setOfferings)
      .catch((e: any) => setErr(errMsg(e, 'No se pudo cargar el catálogo')));
  const loadInstructors = () =>
    api<Instructor[]>('/booking/instructors').then(setInstructors).catch(() => setInstructors([]));
  const loadResourceTypes = () =>
    kind === 'CLASS'
      ? api<ResourceType[]>('/booking/resource-types').then(setResourceTypes).catch(() => setResourceTypes([]))
      : Promise.resolve();

  useEffect(() => { setErr(null); loadSlots(); }, [weekStart, kind, bmActiveBranch]);
  useEffect(() => { loadOfferings(); loadInstructors(); loadResourceTypes(); }, [kind]);

  const visibleSlots = useMemo(() => slots.filter((s) => {
    if (offeringFilter && s.offeringId !== offeringFilter) return false;
    if (instructorFilter) {
      const off = offerings.find((o) => o.id === s.offeringId);
      if ((off?.instructorId ?? '') !== instructorFilter) return false;
    }
    return true;
  }), [slots, offerings, offeringFilter, instructorFilter]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY_MS)),
    [weekStart],
  );
  const slotsByDay = useMemo(() => {
    const map = new Map<number, Slot[]>();
    for (const s of visibleSlots) {
      const day = new Date(s.startsAt).setHours(0, 0, 0, 0);
      const list = map.get(day) ?? [];
      list.push(s); map.set(day, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    return map;
  }, [visibleSlots, weekStart]);

  const weekLabel = `${weekStart.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – ${new Date(weekEnd.getTime() - DAY_MS).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
            <button onClick={() => setView('calendario')}
              className={`rounded-lg px-2.5 py-1 text-[12px] font-medium ${view === 'calendario' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              Calendario
            </button>
            <button onClick={() => setView('lista')}
              className={`rounded-lg px-2.5 py-1 text-[12px] font-medium ${view === 'lista' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              Lista
            </button>
          </div>
          <button className={btnGhost} onClick={() => setWeekStart(mondayOf(new Date()))}>Hoy</button>
          <button className={btnGhost} onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * DAY_MS))}>◀</button>
          <button className={btnGhost} onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * DAY_MS))}>▶</button>
          <span className="text-sm text-slate-500">{weekLabel}</span>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {kind === 'CLASS' && (
              <button className={btnGhost} onClick={() => setShowResourceTypes(true)}>Aulas / Espacios</button>
            )}
            <button className={btnGhost} onClick={() => setShowInstructors(true)}>{L.providerPlural}</button>
            <button className={btnGhost} onClick={() => setShowOfferings(true)}>{copy.offerings}</button>
            <button className={btnPrimary} onClick={() => setShowNewSlot(true)} disabled={offerings.filter(o => o.isActive).length === 0}
                    title={offerings.filter(o => o.isActive).length === 0 ? `Primero crea en "${copy.offerings}"` : undefined}>
              + Nueva {copy.slot}
            </button>
          </div>
        )}
      </div>

      {/* V74 — dropdowns de filtro (client-side) + Mostrar todo */}
      {(offerings.length > 0 || instructors.length > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          <select className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] text-slate-700"
                  value={offeringFilter} onChange={(e) => setOfferingFilter(e.target.value)}>
            <option value="">{L.allOfferings}</option>
            {offerings.filter((o) => o.isActive).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {instructors.some((i) => i.isActive) && (
            <select className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] text-slate-700"
                    value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)}>
              <option value="">{L.allProviders}</option>
              {instructors.filter((i) => i.isActive).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          )}
          {(offeringFilter || instructorFilter) && (
            <button className={btnGhost} onClick={() => { setOfferingFilter(''); setInstructorFilter(''); }}>
              Mostrar todo
            </button>
          )}
        </div>
      )}
      {err && <p className="text-sm text-rose-600">{err}</p>}

      {view === 'lista' ? (
        <div className={card}>
          <div className="divide-y divide-slate-100">
            {visibleSlots.length === 0 && <p className="px-5 py-6 text-sm text-slate-400">{copy.empty}</p>}
            {[...visibleSlots].sort((a, b) => a.startsAt.localeCompare(b.startsAt)).map((s) => {
              const occupied = occupiedOf(s);
              const cap = capacityOf(s);
              const full = occupied >= cap;
              return (
                <button key={s.id} onClick={() => setDetail(s)}
                  className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-left text-sm hover:bg-slate-50">
                  <span className="flex min-w-0 items-center gap-2">
                    {s.offeringColor && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.offeringColor }} />}
                    <span className="truncate font-medium text-slate-700">{s.offeringName}</span>
                    <span className="text-[12px] text-slate-400">{fmtFull(s.startsAt)} · {s.durationMinutes} min{s.instructorName ? ` · ${s.instructorName}` : ''}</span>
                    {s.seriesId && <span className="rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700" title="Generada por una serie recurrente">recurrente</span>}
                    {(s.waitlistCount ?? 0) > 0 && (
                      <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                            title="Personas en la lista de espera">
                        {s.waitlistCount} en espera
                      </span>
                    )}
                  </span>
                  <span className={`text-[12px] font-medium tabular-nums ${full ? 'text-amber-700' : 'text-slate-500'}`}
                        title={typeSummary(s) || undefined}>
                    {s.resourceTypes?.length
                      ? s.resourceTypes.map((t) => `${t.name[0]} ${t.booked}/${t.capacity}`).join(' · ')
                      : `${occupied}/${cap}`}
                    {full ? ' · lleno' : ''}
                    {s.appointmentsCount > 0 ? ` (+${s.appointmentsCount} manual${s.appointmentsCount === 1 ? '' : 'es'})` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
      <div className={`${card} overflow-x-auto`}>
        <div className="grid min-w-[760px] grid-cols-7 divide-x divide-slate-100">
          {days.map((d) => {
            const key = new Date(d).setHours(0, 0, 0, 0);
            const list = slotsByDay.get(key) ?? [];
            const isToday = new Date().setHours(0, 0, 0, 0) === key;
            return (
              <div key={key} className="min-h-[180px]">
                <p className={`border-b border-slate-100 px-2 py-1.5 text-center text-[12px] font-medium ${isToday ? 'bg-slate-900 text-white' : 'text-slate-500'}`}>
                  {fmtDayHead(d)}
                </p>
                <div className="space-y-1 p-1.5">
                  {list.map((s) => {
                    const occupied = occupiedOf(s);
                    const cap = capacityOf(s);
                    const full = occupied >= cap;
                    return (
                      <button key={s.id} onClick={() => setDetail(s)}
                        className={`block w-full rounded-lg border px-2 py-1 text-left text-[11px] hover:opacity-80 ${full ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
                        title={`${s.offeringName}${s.instructorName ? ` · ${s.instructorName}` : ''}${s.seriesId ? ' · recurrente' : ''} · ${occupied}/${cap}${typeSummary(s) ? ` · ${typeSummary(s)}` : ''}${s.appointmentsCount > 0 ? ` (incluye ${s.appointmentsCount} cita${s.appointmentsCount === 1 ? '' : 's'} manual${s.appointmentsCount === 1 ? '' : 'es'})` : ''}${(s.waitlistCount ?? 0) > 0 ? ` · ${s.waitlistCount} en espera` : ''}`}>
                        <span className="flex items-center gap-1">
                          {s.offeringColor && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.offeringColor }} />}
                          <span className="truncate font-medium text-slate-700">{fmtTime(s.startsAt)} {s.offeringName}{s.seriesId ? ' ↻' : ''}</span>
                        </span>
                        <span className={`tabular-nums ${full ? 'text-amber-700' : 'text-slate-400'}`}>
                          {occupied}/{cap}{full ? ' · lleno' : ''}{(s.waitlistCount ?? 0) > 0 ? ` · ⏳${s.waitlistCount}` : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {visibleSlots.length === 0 && <p className="px-5 py-6 text-sm text-slate-400">{copy.empty}</p>}
      </div>
      )}

      {showResourceTypes && (
        <ResourceTypesModal resourceTypes={resourceTypes} onClose={() => setShowResourceTypes(false)}
                            onChanged={() => { loadResourceTypes(); loadOfferings(); loadSlots(); }} />
      )}
      {showInstructors && (
        <InstructorsModal kind={kind} instructors={instructors} onClose={() => setShowInstructors(false)}
                          onChanged={() => { loadInstructors(); loadOfferings(); }} />
      )}
      {showOfferings && (
        <OfferingsModal kind={kind} offerings={offerings} instructors={instructors.filter((i) => i.isActive)}
                        resourceTypes={resourceTypes.filter((t) => t.isActive)}
                        onClose={() => setShowOfferings(false)}
                        onChanged={() => { loadOfferings(); loadSlots(); }} />
      )}
      {showNewSlot && (
        <SlotModal kind={kind} offerings={offerings.filter(o => o.isActive)}
                   instructors={instructors.filter((i) => i.isActive)}
                   onClose={() => setShowNewSlot(false)}
                   onSaved={() => { setShowNewSlot(false); loadSlots(); }} />
      )}
      {detail && (
        <SlotDetailModal slot={detail} kind={kind} canWrite={canWrite} canManage={canManage}
                         offerings={offerings.filter((o) => o.isActive && o.kind === kind)}
                         instructors={instructors.filter((i) => i.isActive)}
                         onClose={() => setDetail(null)}
                         onChanged={() => loadSlots()} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Catálogo de lo reservable (clases / tipos de cita)
// ---------------------------------------------------------------------------
export function OfferingsModal({ kind, offerings, instructors, resourceTypes = [], onClose, onChanged }: {
  kind: 'CLASS' | 'SLOT'; offerings: Offering[]; instructors: Instructor[];
  resourceTypes?: ResourceType[];
  onClose: () => void; onChanged: () => void;
}) {
  const copy = KIND_COPY[kind];
  const [editing, setEditing] = useState<Offering | null>(null);
  const [name, setName] = useState('');
  const [duration, setDuration] = useState(60);
  const [capacity, setCapacity] = useState(kind === 'CLASS' ? 10 : 1);
  const [color, setColor] = useState('#0EA5E9');
  const [serviceId, setServiceId] = useState('');
  const [instructorId, setInstructorId] = useState('');
  // V88 — tipos de espacio asignados a la clase (solo CLASS).
  const [typeIds, setTypeIds] = useState<string[]>([]);
  const [services, setServices] = useState<CatalogService[]>([]);
  // Epic Pagos — config de cobro (solo CLASS; se guarda en endpoint aparte).
  const [payMode, setPayMode] = useState<'none' | 'required'>('none');
  const [price, setPrice] = useState('');
  const [noShowPct, setNoShowPct] = useState(0);
  const [lateHours, setLateHours] = useState<string>(''); // #578 vacio = usa la antelacion del negocio
  // V102 — late fee separado del no-show (default 20% = refund del 80%).
  const [latePct, setLatePct] = useState(20);
  // #578 (D2) — tope de cambios de horario por reserva (texto; vacio = ilimitado).
  const [rescheduleLimit, setRescheduleLimit] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // C2 (#285) — sucursal de la clase/actividad + sucursales del tenant.
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState<BranchOpt[]>([]);
  // Fix Boutique: la sucursal por defecto del editor = la ACTIVA del top-bar
  // (si el admin la eligió arriba, la clase/cita nace en esa sede), no la principal.
  const { user } = useAuth();
  const activeBranch = user?.branch?.activeBranchId ?? '';

  // Fase 4 — solo Boutique (SLOT): catálogo de servicios para ligar el tipo de
  // cita. Si el rol no puede leer el catálogo (403), el selector no se muestra.
  useEffect(() => {
    if (kind !== 'SLOT') return;
    api<CatalogService[]>('/boutique/services').then(setServices).catch(() => setServices([]));
  }, [kind]);
  // C2 — sucursales activas (para el selector; invisible con 1 sucursal).
  useEffect(() => {
    api<BranchOpt[]>('/branches').then((r) => setBranches(r.filter((b) => b.isActive))).catch(() => setBranches([]));
  }, []);

  const startEdit = (o: Offering) => {
    setEditing(o); setName(o.name); setDuration(o.durationMinutes);
    setCapacity(o.defaultCapacity); setColor(o.color ?? '#0EA5E9');
    setServiceId(o.serviceId ?? ''); setInstructorId(o.instructorId ?? '');
    setTypeIds((o.resourceTypes ?? []).map((t) => t.id)); setErr(null);
    setPayMode(o.paymentMode ?? 'none'); setPrice(o.price != null ? String(o.price) : '');
    setNoShowPct(o.noShowChargePct ?? 0); setLateHours(o.lateCancelWindowHours ? String(o.lateCancelWindowHours) : '');
    setLatePct(o.lateCancelChargePct ?? 20);
    setRescheduleLimit(o.rescheduleLimit != null ? String(o.rescheduleLimit) : '');
    setBranchId(o.branchId ?? '');
  };
  const reset = () => {
    setEditing(null); setName(''); setDuration(60); setCapacity(kind === 'CLASS' ? 10 : 1);
    setServiceId(''); setInstructorId(''); setTypeIds([]);
    setPayMode('none'); setPrice(''); setNoShowPct(0); setLateHours(''); setLatePct(20);
    setRescheduleLimit('');
    setBranchId('');
  };
  const toggleType = (id: string) =>
    setTypeIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const pickService = (id: string) => {
    setServiceId(id);
    const svc = services.find((s) => s.id === id);
    // Hereda la duración del servicio (editable después); el precio solo se muestra.
    if (svc?.durationMinutes) setDuration(Number(svc.durationMinutes));
    if (svc && !name.trim()) setName(svc.name);
  };

  const save = async () => {
    if (!name.trim()) { setErr('Falta el nombre'); return; }
    setBusy(true); setErr(null);
    try {
      // Fix #304 — envía la sucursal MOSTRADA (elegida > activa del top-bar >
      // default), no solo si el admin tocó el selector. Antes `branchId &&` no
      // mandaba nada con el selector sin tocar → el backend caía en la principal
      // (o en 'Todas' del top-bar) y la cita Boutique nacía en la sede equivocada.
      const branchToSend = branchId || activeBranch
        || branches.find((b) => b.isDefault)?.id || branches[0]?.id || '';
      const body = {
        name: name.trim(), durationMinutes: duration, defaultCapacity: capacity, color,
        ...(kind === 'SLOT' && { serviceId: serviceId || null }),
        instructorId: instructorId || null,
        // V88 — lista completa (reemplaza la asignación); solo CLASS.
        ...(kind === 'CLASS' && { resourceTypeIds: typeIds }),
        // C2 (#285) — sucursal de la clase/cita (WYSIWYG con el selector).
        ...(branchToSend && { branchId: branchToSend }),
      };
      let offeringId = editing?.id;
      if (editing) await api(`/booking/offerings/${editing.id}`, { method: 'PUT', body });
      else offeringId = (await api<{ id: string }>('/booking/offerings', { method: 'POST', body: { ...body, kind } })).id;
      // Epic Pagos — config de cobro (CLASS y SLOT/Boutique, V104): endpoint
      // aparte, tras crear/editar. Precio vacío en SLOT = usa el del catálogo.
      if (offeringId) {
        await api(`/booking/offerings/${offeringId}/payment`, {
          method: 'PUT',
          body: {
            paymentMode: payMode,
            price: payMode === 'none' || price.trim() === '' ? undefined : Number(price),
            // V203 — en "Pago en sitio" (none) los % se ignoran: se guardan en 0.
            noShowChargePct: payMode === 'required' ? noShowPct : 0,
            lateCancelWindowHours: lateHours.trim() === '' ? 0 : Number(lateHours),
            lateCancelChargePct: payMode === 'required' ? latePct : 0,
            // #578 (D2) — vacio = ilimitado (null).
            rescheduleLimit: rescheduleLimit.trim() === '' ? null : Number(rescheduleLimit),
          },
        });
      }
      reset(); onChanged();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo guardar')); }
    finally { setBusy(false); }
  };
  const toggleActive = async (o: Offering) => {
    try {
      await api(`/booking/offerings/${o.id}`, { method: 'PUT', body: { isActive: !o.isActive } });
      onChanged();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo actualizar')); }
  };

  // #285 A — copy vertical-aware (Boutique = servicio/quién atiende).
  const L = bookingLabels(verticalOfKind(kind));
  // C2 (#285) — sucursal efectiva del editor: la elegida > la ACTIVA del top-bar >
  // la default del tenant. Es la que se MUESTRA y la que se GUARDA (WYSIWYG) → una
  // cita Boutique creada en la sede B nace en B (fix del bug de #304).
  const defaultBranch = branches.find((b) => b.isDefault)?.id ?? branches[0]?.id ?? '';
  const effBranch = branchId || activeBranch || defaultBranch;
  // Aulas elegibles para ESTA sucursal: las de la sede + las compartidas (NULL).
  const eligibleAulas = resourceTypes.filter((t) => !t.branchId || t.branchId === effBranch);

  return (
    <Modal title={copy.offerings} onClose={onClose}>
      <div className="space-y-3">
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {offerings.length === 0 && <p className="text-sm text-slate-400">Aún no hay nada. Crea abajo.</p>}
          {offerings.map((o) => (
            <div key={o.id} className={`flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm ${o.isActive ? '' : 'opacity-50'}`}>
              <button className="flex min-w-0 items-center gap-2 text-left hover:opacity-70" onClick={() => startEdit(o)} title="Editar">
                {o.color && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: o.color }} />}
                <span className="truncate font-medium text-slate-700">{o.name}</span>
                <span className="text-[11px] text-slate-400">
                  {o.durationMinutes} min · {(o.resourceTypes?.length ?? 0) > 0
                    ? o.resourceTypes!.map((t) => `${t.name} (${t.capacity})`).join(' + ')
                    : `${o.kind === 'SLOT' ? 'estaciones' : 'cupo'} ${o.defaultCapacity}`}
                  {o.serviceName && <> · {o.serviceName}{fmtPrice(o.servicePrice, o.serviceCurrency) ? ` (${fmtPrice(o.servicePrice, o.serviceCurrency)})` : ''}</>}
                  {o.instructorName && <> · {o.instructorName}</>}
                </span>
              </button>
              <button className={btnGhost} onClick={() => toggleActive(o)}>{o.isActive ? 'Desactivar' : 'Activar'}</button>
            </div>
          ))}
        </div>
        <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-[12px] font-medium text-slate-500">{editing ? `Editar: ${editing.name}` : copy.newOffering}</p>
          {kind === 'SLOT' && services.length > 0 && (
            <label className="block text-[12px] text-slate-500">Servicio (opcional — hereda la duración y el precio del catálogo)
              <select className={inputCls} value={serviceId} onChange={(e) => pickService(e.target.value)}>
                <option value="">Sin servicio ligado</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{fmtPrice(s.defaultPrice, s.currency) ? ` · ${fmtPrice(s.defaultPrice, s.currency)}` : ''} · {s.durationMinutes} min
                  </option>
                ))}
              </select>
            </label>
          )}
          <input className={inputCls}
                 placeholder={kind === 'SLOT' ? 'Nombre del servicio (ej. Manicure, Corte, Masaje)' : 'Nombre (ej. Yoga, Spinning, Barre)'}
                 value={name} onChange={(e) => setName(e.target.value)} />
          {/* C2 (#285) — sucursal: solo si el tenant tiene 2+ (invisible con 1). */}
          {branches.length > 1 && (
            <label className="block text-[12px] text-slate-500">Sucursal
              <select className={inputCls} value={effBranch}
                onChange={(e) => { setBranchId(e.target.value); setTypeIds([]); }}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          {instructors.length > 0 && (
            <label className="block text-[12px] text-slate-500">{L.providerLabel} (opcional)
              <select className={inputCls} value={instructorId} onChange={(e) => setInstructorId(e.target.value)}>
                <option value="">Sin asignar</option>
                {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>
          )}
          <div className="grid grid-cols-3 gap-2">
            <label className="text-[12px] text-slate-500">Duración (min)
              <input type="number" min={5} className={inputCls} value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
            </label>
            <label className="text-[12px] text-slate-500">{kind === 'CLASS' ? 'Cupo' : 'Estaciones'}
              <input type="number" min={1} className={inputCls} value={capacity} onChange={(e) => setCapacity(Number(e.target.value))}
                     disabled={typeIds.length > 0}
                     title={typeIds.length > 0 ? 'Con tipos de espacio, el cupo es la suma de sus capacidades'
                       : kind === 'SLOT' ? 'Cuántas personas puedes atender al mismo tiempo (ej. sillas o cabinas). Deja 1 si atiendes de una en una.' : undefined} />
            </label>
            <label className="text-[12px] text-slate-500">Color
              <input type="color" className="h-[38px] w-full rounded-xl border border-slate-200" value={color} onChange={(e) => setColor(e.target.value)} />
            </label>
          </div>
          {/* V88 — tipos de espacio de la clase (el cliente elige uno al reservar) */}
          {kind === 'CLASS' && eligibleAulas.length > 0 && (
            <div>
              <p className="text-[12px] text-slate-500">Aulas de la clase{branches.length > 1 ? ' (de esta sucursal + compartidas)' : ''} — plantilla para sesiones nuevas; el cliente elige la suya al reservar</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {eligibleAulas.map((t) => (
                  <button key={t.id} onClick={() => toggleType(t.id)}
                    className={`rounded-lg border px-2 py-1 text-[11px] font-medium ${typeIds.includes(t.id) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'}`}>
                    {t.name} · {t.capacity}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                {typeIds.length > 0
                  ? `Cupo total: ${resourceTypes.filter((t) => typeIds.includes(t.id)).reduce((a, t) => a + t.capacity, 0)} (suma por tipo).`
                  : 'Sin tipos seleccionados, la clase usa el cupo global de siempre.'}
              </p>
            </div>
          )}
          {/* Epic Pagos (V101/V104) — cobro en línea: CLASS (Wellness) y
              SLOT (Boutique; el precio puede venir del catálogo ligado). */}
          {(
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-[12px] font-medium text-slate-600">Cobro en línea</p>
              <label className="mt-1 block text-[12px] text-slate-500">Modo de pago
                {/* V203 — solo dos modos: prepago o pago en sitio (se retiró `optional`). */}
                <select className={inputCls} value={payMode} onChange={(e) => setPayMode(e.target.value as 'none' | 'required')}>
                  <option value="none">Pago en sitio (sin pago en línea)</option>
                  <option value="required">Prepago para confirmar</option>
                </select>
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {/* V203 — precio y % solo con prepago; en "Pago en sitio" se ignoran. */}
                {payMode === 'required' && (
                  <>
                    <label className="text-[12px] text-slate-500">Precio (IVA incl.)
                      <input type="number" min={0} step="0.01" className={inputCls} value={price}
                             onChange={(e) => setPrice(e.target.value)}
                             placeholder={kind === 'SLOT' ? 'Vacío = precio del catálogo' : '0.00'} />
                    </label>
                    {/* V102 — fees separados: no-show (no llegó) vs cancelación tardía. */}
                    <label className="text-[12px] text-slate-500">% cargo no-show
                      <input type="number" min={0} max={100} className={inputCls} value={noShowPct}
                             onChange={(e) => setNoShowPct(Number(e.target.value))} />
                    </label>
                    <label className="text-[12px] text-slate-500">% cancelación tardía
                      <input type="number" min={0} max={100} className={inputCls} value={latePct}
                             onChange={(e) => setLatePct(Number(e.target.value))} />
                    </label>
                  </>
                )}
                {/* Ventana + tope de cambios: aplican en AMBOS modos (gobiernan mover y la
                    marca de cancelación tardía, incluso sin cobro en línea). */}
                <label className="text-[12px] text-slate-500">Ventana sin cargo (horas)
                  <input type="number" min={0} max={720} className={inputCls} value={lateHours}
                         onChange={(e) => setLateHours(e.target.value)}
                         placeholder="Vacío = usa la antelación del negocio" />
                </label>
                {/* #578 (D2) — tope de cambios de horario por reserva (vacio = ilimitado). */}
                <label className="text-[12px] text-slate-500">Cambios de horario permitidos
                  <input type="number" min={0} max={50} className={inputCls} value={rescheduleLimit}
                         onChange={(e) => setRescheduleLimit(e.target.value)}
                         placeholder="Vacío = sin límite" />
                </label>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                {(() => {
                  // V203 — en "Pago en sitio" los % se ignoran: la política no habla de cargos.
                  const effLate = payMode === 'required' ? latePct : 0;
                  const effNoShow = payMode === 'required' ? noShowPct : 0;
                  const txt = cancellationPolicyText({
                    windowHours: lateHours.trim() === '' ? 0 : Number(lateHours), latePct: effLate, noShowPct: effNoShow,
                    rescheduleLimit: rescheduleLimit.trim() === '' ? null : Number(rescheduleLimit),
                  });
                  return txt ? `Tu cliente verá: ${txt}` : 'Sin cargos por cancelar o no asistir: siempre reembolso total.';
                })()}
              </p>
            </div>
          )}
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex justify-end gap-2">
            {editing && <button className={btnGhost} onClick={reset}>Cancelar edición</button>}
            <button className={btnPrimary} onClick={save} disabled={busy}>{editing ? 'Guardar cambios' : 'Crear'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// V88/V91 — AULAS / espacios (Reformer, Mat, Zona…). El aula es un recurso
// PERSISTENTE del admin: capacidad + disciplina/equipo viven en el aula hasta
// que él las reconfigure. Se asignan por clase (plantilla) y el cupo se cuenta
// por aula; V91: un aula solo puede estar en UNA sesión por horario.
// ---------------------------------------------------------------------------
function ResourceTypesModal({ resourceTypes, onClose, onChanged }: {
  resourceTypes: ResourceType[]; onClose: () => void; onChanged: () => void;
}) {
  const [editing, setEditing] = useState<ResourceType | null>(null);
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState(8);
  const [discipline, setDiscipline] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // V93 — editor de mapa del aula seleccionada.
  const [mapFor, setMapFor] = useState<ResourceType | null>(null);
  const editingHasMap = (editing?.spotsCount ?? 0) > 0;
  // C2 (#285) — sucursal del aula ('' = COMPARTIDA/NULL) + sucursales del tenant.
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState<BranchOpt[]>([]);
  useEffect(() => {
    api<BranchOpt[]>('/branches').then((r) => setBranches(r.filter((b) => b.isActive))).catch(() => setBranches([]));
  }, []);

  const startEdit = (t: ResourceType) => {
    setEditing(t); setName(t.name); setCapacity(t.capacity);
    setDiscipline(t.discipline ?? ''); setErr(null); setBranchId(t.branchId ?? '');
  };
  const reset = () => { setEditing(null); setName(''); setCapacity(8); setDiscipline(''); setBranchId(''); };

  const save = async () => {
    if (!name.trim()) { setErr('Falta el nombre'); return; }
    if (!Number.isFinite(capacity) || capacity < 1) { setErr('La capacidad debe ser al menos 1'); return; }
    setBusy(true); setErr(null);
    try {
      // C2 (#285) — branchId null = aula COMPARTIDA (disponible en todas las sedes).
      const branch = { branchId: branchId || null };
      // V93: con mapa, capacity no viaja (la sincroniza el mapa; el backend la rechaza).
      const body = editingHasMap
        ? { name: name.trim(), discipline: discipline.trim(), ...branch }
        : { name: name.trim(), capacity, discipline: discipline.trim(), ...branch };
      if (editing) await api(`/booking/resource-types/${editing.id}`, { method: 'PUT', body });
      else await api('/booking/resource-types', { method: 'POST', body });
      reset(); onChanged();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo guardar')); }
    finally { setBusy(false); }
  };
  const toggleActive = async (t: ResourceType) => {
    try {
      await api(`/booking/resource-types/${t.id}`, { method: 'PUT', body: { isActive: !t.isActive } });
      onChanged();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo actualizar')); }
  };

  return (
    <Modal title="Aulas / Espacios" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-[12px] text-slate-400">
          Define tus aulas/espacios físicos (ej. Sala Reformer 8, Sala Mat 10, Zona A 5) con su equipo o
          disciplina. Asigna las aulas a cada clase; el cliente elige la suya al reservar, el cupo se cuenta
          por aula y <b>un aula solo puede tener una sesión a la vez</b> (el sistema rechaza traslapes).
        </p>
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {resourceTypes.length === 0 && <p className="text-sm text-slate-400">Aún no hay aulas. Crea abajo.</p>}
          {resourceTypes.map((t) => (
            <div key={t.id} className={`flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm ${t.isActive ? '' : 'opacity-50'}`}>
              <button className="min-w-0 text-left hover:opacity-70" onClick={() => startEdit(t)} title="Editar">
                <span className="block truncate font-medium text-slate-700">{t.name}</span>
                <span className="block text-[11px] text-slate-400">
                  capacidad {t.capacity}{t.discipline ? ` · ${t.discipline}` : ''}
                  {t.branchId ? ` · ${t.branchName ?? 'sucursal'}` : ' · compartida'}
                  {(t.spotsCount ?? 0) > 0 ? ` · mapa: ${t.spotsCount} lugar${t.spotsCount === 1 ? '' : 'es'}` : ''}
                  {(t.offeringsCount ?? 0) > 0 ? ` · en ${t.offeringsCount} clase${t.offeringsCount === 1 ? '' : 's'}` : ''}
                </span>
              </button>
              <span className="flex shrink-0 items-center gap-1.5">
                {/* V93 — plano del aula: lugares nombrados y posicionados. */}
                <button className={btnGhost} onClick={() => setMapFor(t)}>
                  {(t.spotsCount ?? 0) > 0 ? 'Mapa' : '+ Mapa'}
                </button>
                <button className={btnGhost} onClick={() => toggleActive(t)}>{t.isActive ? 'Desactivar' : 'Activar'}</button>
              </span>
            </div>
          ))}
        </div>
        <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-[12px] font-medium text-slate-500">{editing ? `Editar: ${editing.name}` : '+ Nueva aula / espacio'}</p>
          <div className="grid grid-cols-2 gap-2">
            <input className={inputCls} placeholder='Nombre (ej. "Sala Reformer")' value={name}
                   onChange={(e) => setName(e.target.value)} maxLength={100} />
            <label className="text-[12px] text-slate-500">Capacidad (unidades)
              {/* V93 (D2): con mapa, la capacidad la define el mapa (lugares activos). */}
              <input type="number" min={1} max={1000} className={inputCls} value={capacity}
                     disabled={editingHasMap}
                     title={editingHasMap ? 'La capacidad la define el mapa de lugares' : undefined}
                     onChange={(e) => setCapacity(Number(e.target.value))} />
              {editingHasMap && (
                <span className="block text-[10px] text-slate-400">La define el mapa ({editing!.spotsCount} lugares)</span>
              )}
            </label>
          </div>
          <input className={inputCls} placeholder='Equipo / disciplina (ej. "reformers", "mats", "bicis") — opcional'
                 value={discipline} onChange={(e) => setDiscipline(e.target.value)} maxLength={100} />
          {/* C2 (#285) — sucursal del aula: solo con 2+ sedes (invisible con 1). */}
          {branches.length > 1 && (
            <label className="block text-[12px] text-slate-500">Sucursal del aula
              <select className={inputCls} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Compartida (todas las sucursales)</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex justify-end gap-2">
            {editing && <button className={btnGhost} onClick={reset}>Cancelar edición</button>}
            <button className={btnPrimary} onClick={save} disabled={busy}>{editing ? 'Guardar cambios' : 'Crear'}</button>
          </div>
        </div>
      </div>
      {mapFor && (
        <RoomMapEditor room={mapFor} onClose={() => setMapFor(null)}
                       onChanged={() => { setMapFor(null); reset(); onChanged(); }} />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// V93 (Fase 6) — editor de MAPA del aula: grid touch-first con lugares
// nombrados. Tap en celda vacía = agregar lugar (etiqueta auto, editable);
// tap en lugar = seleccionar (renombrar / mover tap-tap / quitar). Guardar =
// PUT bulk (el backend valida duplicados, aplica el guardrail de reservas
// futuras y sincroniza capacity = lugares activos). Drag & drop / coords
// libres = follow-up en PR separado (decisión Arturo 2026-07-22).
// ---------------------------------------------------------------------------
// V94 (Fase 6.1): el editor pasa de grid a LIENZO LIBRE 80×80 (snap 1/4 de
// celda, footprint 4×4) con drag por Pointer Events + orientación del aula
// (frente/espejos/entrada). La colisión viva usa spotsCollide — el MISMO
// algoritmo del 400 del backend (condición #1 del diseño 6.1).
// Bug 2 (2026-07-22): el canvas ya no es de px fijos (960px desbordaba el
// modal y el móvil) — se escala al contenedor (tope ~60vh/640px) y TODO se
// posiciona en %; el drag deriva la unidad de getBoundingClientRect().
// Bug 2, 2ª vuelta: la VISTA se recorta al bounding-box de los lugares + un
// margen fijo de arrastre (2 celdas por lado, alineado a celda) — mostrar el
// lienzo 80×80 completo volvía ilegible un mapa de una columna. La vista
// CRECE al soltar/nudgear un lugar hacia el margen (el bbox se recalcula por
// estado). Tile mínimo legible: si el contenedor no alcanza, scroll (último
// recurso). Los marcadores de orientación se proyectan sobre el borde
// VISIBLE (misma aproximación documentada del picker).

/** "reformers" → "Reformer", "mats" → "Mat" (base de la etiqueta auto). */
function spotLabelBase(room: ResourceType): string {
  const raw = (room.discipline ?? '').trim() || room.name.trim() || 'Lugar';
  const singular = raw.length > 3 && /s$/i.test(raw) ? raw.slice(0, -1) : raw;
  return singular.charAt(0).toUpperCase() + singular.slice(1);
}

type EditorSpot = { id?: string; label: string; x: number; y: number; isActive: boolean };
type EditorMark = { type: 'mirror' | 'door'; edge: LayoutEdge; start: number; len: number };

function RoomMapEditor({ room, onClose, onChanged }: {
  room: ResourceType; onClose: () => void; onChanged: () => void;
}) {
  const [spots, setSpots] = useState<EditorSpot[]>([]);
  const [front, setFront] = useState<LayoutEdge | ''>('');
  const [marks, setMarks] = useState<EditorMark[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [selMark, setSelMark] = useState<number | null>(null);
  const [showOrient, setShowOrient] = useState(false);
  const [drag, setDrag] = useState<{ idx: number; x: number; y: number; colliding: boolean } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const gesture = useRef<{ idx: number; startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null);

  useEffect(() => {
    api<{ layoutConfig: RoomLayout; spots: Array<{ id: string; label: string; x?: number; y?: number; row: number; col: number; isActive: boolean }> }>(
      `/booking/resource-types/${room.id}/spots`)
      .then((r) => {
        setSpots(r.spots.filter((s) => s.isActive).map((s) => ({
          id: s.id, label: s.label,
          x: s.x ?? s.col * SPOT_UNITS, y: s.y ?? s.row * SPOT_UNITS,
          isActive: true,
        })));
        setFront(r.layoutConfig?.front ?? '');
        setMarks((r.layoutConfig?.marks ?? []) as EditorMark[]);
        setLoaded(true);
      })
      .catch((e: any) => setErr(errMsg(e, 'No se pudo cargar el mapa')));
  }, [room.id]);

  const clampU = (v: number) => Math.max(0, Math.min(CANVAS_UNITS - SPOT_UNITS, v));
  const collidesAt = (x: number, y: number, exceptIdx: number) =>
    spots.some((s, i) => i !== exceptIdx && s.isActive && spotsCollide({ x, y }, s));

  // Ventana visible: bbox de los lugares + 2 celdas de margen por lado
  // (alineada a celda: el punteado de fondo cuadra sin offset). Vacío = 8×8.
  const DRAG_MARGIN = 2 * SPOT_UNITS;
  const view = useMemo(() => {
    if (spots.length === 0) return { x0: 0, y0: 0, w: 32, h: 32 };
    const cell = (v: number, f: (n: number) => number) => f(v / SPOT_UNITS) * SPOT_UNITS;
    const x0 = Math.max(0, cell(Math.min(...spots.map((s) => s.x)) - DRAG_MARGIN, Math.floor));
    const y0 = Math.max(0, cell(Math.min(...spots.map((s) => s.y)) - DRAG_MARGIN, Math.floor));
    const x1 = Math.min(CANVAS_UNITS, cell(Math.max(...spots.map((s) => s.x + SPOT_UNITS)) + DRAG_MARGIN, Math.ceil));
    const y1 = Math.min(CANVAS_UNITS, cell(Math.max(...spots.map((s) => s.y + SPOT_UNITS)) + DRAG_MARGIN, Math.ceil));
    return { x0, y0, w: x1 - x0, h: y1 - y0 };
  }, [spots]);

  const nextLabel = () => {
    const base = spotLabelBase(room);
    const re = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+)$`, 'i');
    const max = spots.reduce((m, s) => {
      const match = s.label.match(re);
      return match ? Math.max(m, Number(match[1])) : m;
    }, 0);
    return `${base} ${max + 1}`;
  };

  /** Posición en unidades (esquina del footprint centrado bajo el dedo).
   *  La unidad en px se deriva del ancho REAL del canvas y de la VISTA
   *  recortada (durante un drag la vista no cambia: solo se recalcula al
   *  soltar, así el mapeo es estable bajo el dedo). */
  const unitsFromPointer = (e: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const unit = rect.width / view.w;
    return {
      x: clampU(Math.round(view.x0 + (e.clientX - rect.left) / unit - SPOT_UNITS / 2)),
      y: clampU(Math.round(view.y0 + (e.clientY - rect.top) / unit - SPOT_UNITS / 2)),
    };
  };

  // --- Drag (Pointer Events: touch + mouse unificados) -----------------------
  const onSpotPointerDown = (idx: number) => (e: React.PointerEvent<HTMLButtonElement>) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    gesture.current = {
      idx, startX: e.clientX, startY: e.clientY,
      origX: spots[idx].x, origY: spots[idx].y, moved: false,
    };
  };
  const onSpotPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const g = gesture.current;
    if (!g) return;
    if (!g.moved && Math.hypot(e.clientX - g.startX, e.clientY - g.startY) < 6) return;
    g.moved = true;
    const p = unitsFromPointer(e);
    setDrag({ idx: g.idx, x: p.x, y: p.y, colliding: collidesAt(p.x, p.y, g.idx) });
  };
  const onSpotPointerUp = () => {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (!g.moved) {
      // Tap: seleccionar/des-seleccionar (renombrar/quitar/nudge).
      setSel((cur) => (cur === g.idx ? null : g.idx));
      setDrag(null);
      return;
    }
    setDrag((d) => {
      if (d && !d.colliding) {
        setSpots((prev) => prev.map((s, i) => (i === d.idx ? { ...s, x: d.x, y: d.y } : s)));
      }
      return null; // colisión → vuelve al origen
    });
  };

  const tapCanvas = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== canvasRef.current) return; // solo lienzo vacío
    setErr(null);
    const p = unitsFromPointer(e);
    if (collidesAt(p.x, p.y, -1)) { setSel(null); return; }
    setSpots((prev) => [...prev, { label: nextLabel(), x: p.x, y: p.y, isActive: true }]);
    setSel(spots.length);
  };

  const nudge = (dx: number, dy: number) => {
    if (sel == null) return;
    const s = spots[sel];
    const nx = clampU(s.x + dx), ny = clampU(s.y + dy);
    if (collidesAt(nx, ny, sel)) return;
    setSpots((prev) => prev.map((p, i) => (i === sel ? { ...p, x: nx, y: ny } : p)));
  };
  const removeSelected = () => {
    if (sel == null) return;
    setSpots((prev) => prev.filter((_, i) => i !== sel));
    setSel(null);
  };
  const renameSelected = (label: string) => {
    if (sel == null) return;
    setSpots((prev) => prev.map((s, i) => (i === sel ? { ...s, label } : s)));
  };

  // --- Orientación ------------------------------------------------------------
  const addMark = (type: 'mirror' | 'door') => {
    const edge: LayoutEdge = (front || 'top') as LayoutEdge;
    const mark: EditorMark = type === 'mirror'
      ? { type, edge, start: 8, len: 64 }
      : { type, edge: edge === 'left' ? 'right' : 'left', start: 36, len: 8 };
    setMarks((prev) => [...prev, mark]);
    setSelMark(marks.length);
  };
  const patchMark = (patch: Partial<EditorMark>) => {
    if (selMark == null) return;
    setMarks((prev) => prev.map((m, i) => {
      if (i !== selMark) return m;
      const next = { ...m, ...patch };
      next.start = Math.max(0, Math.min(79, next.start));
      next.len = Math.max(4, Math.min(CANVAS_UNITS - next.start, next.len));
      return next;
    }));
  };

  const layout: RoomLayout = (front || marks.length)
    ? { ...(front ? { front: front as LayoutEdge } : {}), ...(marks.length ? { marks } : {}) }
    : null;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/booking/resource-types/${room.id}/spots`, {
        method: 'PUT',
        body: {
          spots: spots.map((s, i) => ({ ...(s.id ? { id: s.id } : {}), label: s.label, x: s.x, y: s.y, displayOrder: i, isActive: true })),
          layoutConfig: layout,
        },
      });
      onChanged();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo guardar el mapa')); setBusy(false); }
  };

  const selected = sel != null ? spots[sel] : null;
  // Mapeo a la VISTA recortada (no al lienzo completo).
  const pctX = (u: number) => `${((u - view.x0) / view.w) * 100}%`;
  const pctY = (u: number) => `${((u - view.y0) / view.h) * 100}%`;
  const pctW = (u: number) => `${(u / view.w) * 100}%`;
  const pctH = (u: number) => `${(u / view.h) * 100}%`;
  // Tile mínimo LEGIBLE (~52px: etiqueta a 2 líneas sin truncar). Si el
  // contenedor no da, el wrapper scrollea (último recurso).
  const MIN_TILE_PX = 52;
  const minCanvasPx = Math.ceil((view.w / SPOT_UNITS) * MIN_TILE_PX);
  return (
    <Modal title={`Mapa: ${room.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-[12px] text-slate-400">
          <b>Arrastra</b> los lugares al acomodo real de la sala; toca el lienzo vacío para
          <b> agregar</b> un lugar y toca un lugar para renombrarlo o quitarlo. La capacidad
          del aula = número de lugares ({spots.length} ahora). En "Orientación" marcas el
          frente, los espejos y la entrada — el cliente verá el plano orientado.
        </p>
        {loaded && (
          <div className="overflow-auto rounded-xl border border-slate-200">
            <div ref={canvasRef} onClick={tapCanvas}
                 className="relative rounded-xl bg-slate-50"
                 style={{ width: `min(100%, ${Math.round((view.w / view.h) * 58)}vh, 640px)`,
                          minWidth: `${minCanvasPx}px`,
                          aspectRatio: `${view.w} / ${view.h}`,
                          backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
                          backgroundSize: `${(SPOT_UNITS / view.w) * 100}% ${(SPOT_UNITS / view.h) * 100}%` }}>
              <LayoutChrome layout={layout} />
              {spots.map((s, i) => {
                const dragging = drag?.idx === i;
                const x = dragging ? drag.x : s.x, y = dragging ? drag.y : s.y;
                return (
                  <button key={s.id ?? `new-${i}`} type="button"
                    onPointerDown={onSpotPointerDown(i)}
                    onPointerMove={onSpotPointerMove}
                    onPointerUp={onSpotPointerUp}
                    onPointerCancel={onSpotPointerUp}
                    style={{
                      position: 'absolute', touchAction: 'none',
                      left: pctX(x), top: pctY(y),
                      width: pctW(SPOT_UNITS), height: pctH(SPOT_UNITS),
                    }}
                    className={`flex items-center justify-center rounded-lg border px-0.5 text-[10px] font-medium leading-tight select-none
                      ${dragging
                        ? drag!.colliding
                          ? 'z-10 border-rose-500 bg-rose-100 text-rose-700'
                          : 'z-10 border-emerald-500 bg-emerald-50 text-emerald-700'
                        : sel === i
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-sky-300 bg-sky-50 text-sky-800'}`}>
                    {/* Legibilidad (Bug 2, 2ª vuelta): etiqueta completa a 2
                        líneas — con el tile mínimo de 52px ya no se trunca. */}
                    <span className="overflow-hidden break-words text-center"
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {selected && (
          <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-100 bg-slate-50 p-2">
            <input className="w-40 rounded-xl border border-slate-200 px-3 py-1.5 text-sm" value={selected.label} maxLength={50}
                   onChange={(e) => renameSelected(e.target.value)} />
            <span className="flex items-center gap-1">
              <button className={btnGhost} onClick={() => nudge(-1, 0)} title="1/4 de celda">◀</button>
              <button className={btnGhost} onClick={() => nudge(0, -1)}>▲</button>
              <button className={btnGhost} onClick={() => nudge(0, 1)}>▼</button>
              <button className={btnGhost} onClick={() => nudge(1, 0)}>▶</button>
            </span>
            <button className="rounded-lg border border-rose-200 bg-white px-2.5 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50"
                    onClick={removeSelected}>
              Quitar
            </button>
          </div>
        )}
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-2">
          <button className="text-[12px] font-medium text-slate-600" onClick={() => setShowOrient((v) => !v)}>
            {showOrient ? '▾' : '▸'} Orientación del aula {layout ? '· configurada' : ''}
          </button>
          {showOrient && (
            <div className="mt-2 space-y-2">
              <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-slate-500">
                Frente:
                {(['top', 'bottom', 'left', 'right'] as LayoutEdge[]).map((e) => (
                  <button key={e} className={`${btnGhost} ${front === e ? 'border-slate-900 bg-slate-900 text-white hover:bg-slate-900' : ''}`}
                          onClick={() => setFront(front === e ? '' : e)}>
                    {{ top: 'Arriba', bottom: 'Abajo', left: 'Izquierda', right: 'Derecha' }[e]}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button className={btnGhost} onClick={() => addMark('mirror')}>+ Espejos</button>
                <button className={btnGhost} onClick={() => addMark('door')}>+ Entrada</button>
              </div>
              {marks.map((m, i) => (
                <div key={i}
                     className={`flex flex-wrap items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] ${selMark === i ? 'border-slate-400 bg-white' : 'border-slate-200'}`}
                     onClick={() => setSelMark(i)}>
                  <span className="font-medium text-slate-600">{m.type === 'mirror' ? 'Espejos' : 'Entrada'}</span>
                  <select className="rounded-lg border border-slate-200 px-1.5 py-0.5 text-[11px]" value={m.edge}
                          onChange={(e) => { setSelMark(i); setMarks((prev) => prev.map((x, j) => (j === i ? { ...x, edge: e.target.value as LayoutEdge } : x))); }}>
                    <option value="top">Arriba</option><option value="bottom">Abajo</option>
                    <option value="left">Izquierda</option><option value="right">Derecha</option>
                  </select>
                  <span className="flex items-center gap-1">
                    <button className={btnGhost} onClick={() => { setSelMark(i); patchMark({ start: m.start - 4 }); }}>◀</button>
                    <button className={btnGhost} onClick={() => { setSelMark(i); patchMark({ start: m.start + 4 }); }}>▶</button>
                    <button className={btnGhost} onClick={() => { setSelMark(i); patchMark({ len: m.len - 4 }); }}>−</button>
                    <button className={btnGhost} onClick={() => { setSelMark(i); patchMark({ len: m.len + 4 }); }}>+</button>
                  </span>
                  <button className="text-rose-600" onClick={() => { setMarks((prev) => prev.filter((_, j) => j !== i)); setSelMark(null); }}>Quitar</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {err && <p className="text-sm text-rose-600">{err}</p>}
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-400">
            Quitar un lugar con reservas futuras se rechaza (reubícalas primero).
          </p>
          <button className={btnPrimary} onClick={save} disabled={busy || !loaded}>Guardar mapa</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// V74 (Fase 5, Task C) — catálogo de instructores (nombre + bio + activo)
// ---------------------------------------------------------------------------
export function InstructorsModal({ kind = 'CLASS', instructors, onClose, onChanged }: {
  kind?: 'CLASS' | 'SLOT'; instructors: Instructor[]; onClose: () => void; onChanged: () => void;
}) {
  // #285 P2 — Boutique nombra "quién atiende / personal"; Wellness "instructor/a".
  const L = bookingLabels(verticalOfKind(kind));
  const [editing, setEditing] = useState<Instructor | null>(null);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const startEdit = (i: Instructor) => { setEditing(i); setName(i.name); setBio(i.bio ?? ''); setErr(null); };
  const reset = () => { setEditing(null); setName(''); setBio(''); };

  const save = async () => {
    if (!name.trim()) { setErr('Falta el nombre'); return; }
    setBusy(true); setErr(null);
    try {
      const body = { name: name.trim(), ...(bio.trim() ? { bio: bio.trim() } : { bio: '' }) };
      if (editing) await api(`/booking/instructors/${editing.id}`, { method: 'PUT', body });
      else await api('/booking/instructors', { method: 'POST', body });
      reset(); onChanged();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo guardar')); }
    finally { setBusy(false); }
  };
  const toggleActive = async (i: Instructor) => {
    try {
      await api(`/booking/instructors/${i.id}`, { method: 'PUT', body: { isActive: !i.isActive } });
      onChanged();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo actualizar')); }
  };

  return (
    <Modal title={L.providerPlural} onClose={onClose}>
      <div className="space-y-3">
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {instructors.length === 0 && <p className="text-sm text-slate-400">Aún no hay {L.providerPlural.toLowerCase()}. Crea abajo.</p>}
          {instructors.map((i) => (
            <div key={i.id} className={`flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm ${i.isActive ? '' : 'opacity-50'}`}>
              <button className="min-w-0 text-left hover:opacity-70" onClick={() => startEdit(i)} title="Editar">
                <span className="block truncate font-medium text-slate-700">{i.name}</span>
                {i.bio && <span className="block truncate text-[11px] text-slate-400">{i.bio}</span>}
              </button>
              <button className={btnGhost} onClick={() => toggleActive(i)}>{i.isActive ? 'Desactivar' : 'Activar'}</button>
            </div>
          ))}
        </div>
        <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
          <p className="text-[12px] font-medium text-slate-500">{editing ? `Editar: ${editing.name}` : `+ Nuevo · ${L.providerLabel}`}</p>
          <input className={inputCls} placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} maxLength={150} />
          <textarea className={inputCls} placeholder="Biografía (se muestra en la página pública)" rows={3}
                    value={bio} onChange={(e) => setBio(e.target.value)} maxLength={4000} />
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex justify-end gap-2">
            {editing && <button className={btnGhost} onClick={reset}>Cancelar edición</button>}
            <button className={btnPrimary} onClick={save} disabled={busy}>{editing ? 'Guardar cambios' : 'Crear'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Nueva sesión / horario (+ repetición semanal — D6)
// ---------------------------------------------------------------------------
const WEEKDAY_CHIPS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export function SlotModal({ kind, offerings, instructors = [], onClose, onSaved }: {
  kind: 'CLASS' | 'SLOT'; offerings: Offering[]; instructors?: Instructor[];
  onClose: () => void; onSaved: () => void;
}) {
  const copy = KIND_COPY[kind];
  const L = bookingLabels(verticalOfKind(kind));
  const [offeringId, setOfferingId] = useState(offerings[0]?.id ?? '');
  // V91 — instructor de la sesión (default: el de la clase; '' = sin instructor).
  const [instructorId, setInstructorId] = useState(offerings[0]?.instructorId ?? '');
  const pickOffering = (id: string) => {
    setOfferingId(id);
    setInstructorId(offerings.find((o) => o.id === id)?.instructorId ?? '');
  };
  const [startsAt, setStartsAt] = useState('');
  const [duration, setDuration] = useState<number | ''>('');
  const [capacity, setCapacity] = useState<number | ''>('');
  // Modos de repetición: ninguna | semanal simple (legado) | serie por días (V74).
  const [mode, setMode] = useState<'una' | 'semanal' | 'serie'>('una');
  const [repeatWeeks, setRepeatWeeks] = useState(0);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [untilKind, setUntilKind] = useState<'fecha' | 'total'>('fecha');
  const [repeatUntil, setRepeatUntil] = useState('');
  const [occurrences, setOccurrences] = useState(9);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const selected = offerings.find((o) => o.id === offeringId);

  const toggleWeekday = (d: number) =>
    setWeekdays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());

  const save = async () => {
    if (!offeringId || !startsAt) { setErr('Elige la actividad y el horario'); return; }
    if (mode === 'serie') {
      if (weekdays.length === 0) { setErr('Elige al menos un día de la semana'); return; }
      if (untilKind === 'fecha' && !repeatUntil) { setErr('Indica la fecha en que termina la serie'); return; }
    }
    setBusy(true); setErr(null);
    try {
      await api('/booking/slots', {
        method: 'POST',
        body: {
          offeringId,
          // V91: '' = explícitamente sin instructor (el DTO lo vuelve null).
          instructorId,
          startsAt: new Date(startsAt).toISOString(),
          ...(duration !== '' && { durationMinutes: duration }),
          ...(capacity !== '' && { capacity }),
          ...(mode === 'semanal' && repeatWeeks > 0 && { repeatWeeks }),
          ...(mode === 'serie' && {
            weekdays,
            ...(untilKind === 'fecha' ? { repeatUntil } : { occurrences }),
          }),
        },
      });
      onSaved();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo crear')); setBusy(false); }
  };

  return (
    <Modal title={`Nueva ${copy.slot}`} onClose={onClose}>
      <div className="space-y-3">
        <label className="block text-[12px] text-slate-500">Clase / actividad
          <select className={inputCls} value={offeringId} onChange={(e) => pickOffering(e.target.value)}>
            {offerings.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        {instructors.length > 0 && (
          <label className="block text-[12px] text-slate-500">{L.providerLabel} de {kind === 'SLOT' ? 'la cita' : 'la sesión'}
            <select className={inputCls} value={instructorId} onChange={(e) => setInstructorId(e.target.value)}>
              <option value="">Sin asignar</option>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        )}
        {(selected?.resourceTypes?.length ?? 0) > 0 && (
          <p className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
            Aulas de esta sesión (de la clase): {selected!.resourceTypes!.map((t) => t.name).join(' + ')}.
            El sistema rechazará el horario si otra sesión ya ocupa alguna de estas aulas o al instructor.
          </p>
        )}
        <label className="block text-[12px] text-slate-500">{mode === 'serie' ? 'Primera fecha y hora de la serie' : 'Fecha y hora'}
          <input type="datetime-local" className={inputCls} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[12px] text-slate-500">Duración (min)
            <input type="number" min={5} className={inputCls} placeholder={`${selected?.durationMinutes ?? 60}`}
                   value={duration} onChange={(e) => setDuration(e.target.value === '' ? '' : Number(e.target.value))} />
          </label>
          <label className="text-[12px] text-slate-500">{kind === 'CLASS' ? 'Cupo' : 'Estaciones'}
            <input type="number" min={1} className={inputCls} placeholder={`${selected?.defaultCapacity ?? 1}`}
                   value={capacity} onChange={(e) => setCapacity(e.target.value === '' ? '' : Number(e.target.value))} />
          </label>
        </div>

        {/* V74 — repetición */}
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
          <div className="mb-2 inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
            {([['una', 'Una vez'], ['semanal', 'Cada semana'], ['serie', 'Días fijos']] as const).map(([m, label]) => (
              <button key={m} onClick={() => setMode(m)}
                className={`rounded-lg px-2.5 py-1 text-[12px] font-medium ${mode === m ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                {label}
              </button>
            ))}
          </div>
          {mode === 'semanal' && (
            <label className="block text-[12px] text-slate-500">Semanas adicionales (mismo día/hora)
              <input type="number" min={0} max={26} className={inputCls} value={repeatWeeks} onChange={(e) => setRepeatWeeks(Number(e.target.value))} />
            </label>
          )}
          {mode === 'serie' && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {WEEKDAY_CHIPS.map((label, d) => (
                  <button key={d} onClick={() => toggleWeekday(d)}
                    className={`rounded-lg border px-2 py-1 text-[11px] font-medium ${weekdays.includes(d) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[12px] text-slate-500">Termina
                  <select className={inputCls} value={untilKind} onChange={(e) => setUntilKind(e.target.value as 'fecha' | 'total')}>
                    <option value="fecha">En una fecha</option>
                    <option value="total">Tras N sesiones</option>
                  </select>
                </label>
                {untilKind === 'fecha' ? (
                  <label className="text-[12px] text-slate-500">Fecha fin (inclusive)
                    <input type="date" className={inputCls} value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} />
                  </label>
                ) : (
                  <label className="text-[12px] text-slate-500">Total de sesiones (máx 90)
                    <input type="number" min={1} max={90} className={inputCls} value={occurrences} onChange={(e) => setOccurrences(Number(e.target.value))} />
                  </label>
                )}
              </div>
              <p className="text-[11px] text-slate-400">
                Cada sesión generada es independiente (se edita/cancela una por una). Máx 90 sesiones / 6 meses por serie.
              </p>
            </div>
          )}
        </div>

        {err && <p className="text-sm text-rose-600">{err}</p>}
        <div className="flex justify-end">
          <button className={btnPrimary} onClick={save} disabled={busy}>
            Crear{mode === 'semanal' && repeatWeeks > 0 ? ` (${repeatWeeks + 1} sesiones)` : mode === 'serie' ? ' serie' : ''}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Panel de la sesión: cupos + asistentes (con no-shows) + acciones staff
// ---------------------------------------------------------------------------
export function SlotDetailModal({ slot, kind, canWrite, canManage, offerings, instructors = [], onClose, onChanged }: {
  slot: Slot; kind: 'CLASS' | 'SLOT'; canWrite: boolean; canManage: boolean;
  offerings: Offering[]; instructors?: Instructor[]; onClose: () => void; onChanged: () => void;
}) {
  const [detail, setDetail] = useState<SlotDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [moving, setMoving] = useState<Attendee | null>(null);
  const [editing, setEditing] = useState(false);
  const [slotView, setSlotView] = useState<Slot>(slot);
  const [cancelled, setCancelled] = useState(slot.status === 'CANCELLED');

  const load = () =>
    api<SlotDetail>(`/booking/slots/${slot.id}/attendees`)
      .then(setDetail)
      .catch((e: any) => setErr(errMsg(e, 'No se pudo cargar la sesión')));
  useEffect(() => { load(); }, [slot.id]);

  const act = async (fn: () => Promise<unknown>, fallback: string) => {
    setErr(null);
    try { await fn(); await load(); onChanged(); }
    catch (e: any) { setErr(errMsg(e, fallback)); }
  };
  const setStatus = (b: Attendee, status: 'BOOKED' | 'ATTENDED' | 'NO_SHOW') =>
    act(() => api(`/booking/bookings/${b.id}/status`, { method: 'PATCH', body: { status } }), 'No se pudo actualizar');
  const cancelBooking = (b: Attendee) =>
    act(() => api(`/booking/bookings/${b.id}/cancel`, { method: 'PATCH', body: {} }), 'No se pudo cancelar');
  const refundBooking = (b: Attendee) => {
    if (!window.confirm(`¿Reembolsar el pago de ${b.customerName}? El dinero se devuelve vía tu cuenta de Stripe.`)) return;
    return act(() => api(`/payments/bookings/${b.id}/refund`, { method: 'POST' }), 'No se pudo reembolsar');
  };
  const cancelSlot = () => {
    if (!window.confirm('¿Cancelar la sesión? Sus reservas vivas se cancelan, se libera el cupo y cada cliente recibe un correo de cancelación.')) return;
    return act(() => api(`/booking/slots/${slot.id}/cancel`, { method: 'POST', body: {} }), 'No se pudo cancelar la sesión')
      .then(() => setCancelled(true));
  };

  return (
    <Modal title={`${slotView.offeringName} · ${fmtFull(slotView.startsAt)}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-500">
            {cancelled
              ? <span className="font-medium text-rose-600">Sesión cancelada</span>
              : <>Cupo: <span className="font-medium tabular-nums text-slate-700">{detail?.bookedCount ?? slotView.bookedCount}/{detail?.capacity ?? capacityOf(slotView)}</span>
                 · libres: <span className="font-medium tabular-nums text-slate-700">{detail?.freeCount ?? Math.max(0, capacityOf(slotView) - occupiedOf(slotView))}</span>
                 {(detail?.appointmentsCount ?? slotView.appointmentsCount ?? 0) > 0 && (
                   <span className="ml-1 rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                         title="Citas manuales de la Agenda que ocupan estación en esta ventana (fix D4)">
                     +{detail?.appointmentsCount ?? slotView.appointmentsCount} cita{(detail?.appointmentsCount ?? slotView.appointmentsCount) === 1 ? '' : 's'} manual{(detail?.appointmentsCount ?? slotView.appointmentsCount) === 1 ? '' : 'es'}
                   </span>
                 )}</>}
          </p>
          {!cancelled && (
            <span className="flex items-center gap-2">
              {/* Editar sesión = operación de mostrador (bookings:write, decisión 2026-07-04). */}
              {canWrite && <button className={btnGhost} onClick={() => setEditing(true)}>Editar sesión</button>}
              {canManage && (
                <button className="rounded-lg border border-rose-200 bg-white px-2.5 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50" onClick={cancelSlot}>
                  Cancelar sesión
                </button>
              )}
            </span>
          )}
        </div>
        {/* V88/V91 — ocupación por AULA de la sesión. V90: "apartado" = hold
            de una oferta activa del waitlist (cuenta como ocupación). */}
        {(detail?.resourceTypes?.length ?? 0) > 0 && !cancelled && (
          <div className="flex flex-wrap gap-1.5">
            {detail!.resourceTypes!.map((t) => {
              const held = t.held ?? 0;
              const tFull = t.booked + held >= t.capacity;
              return (
                <span key={t.id}
                  className={`rounded-lg border px-2 py-1 text-[11px] font-medium tabular-nums ${tFull ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                  {t.name} {t.booked}/{t.capacity}
                  {held > 0 ? ` · ${held} apartado${held === 1 ? '' : 's'}` : ''}
                  {tFull ? ' · lleno' : ''}
                </span>
              );
            })}
          </div>
        )}
        {(detail?.heldCount ?? 0) > 0 && (detail?.resourceTypes?.length ?? 0) === 0 && !cancelled && (
          <p className="text-[11px] text-amber-700">
            {detail!.heldCount} lugar{detail!.heldCount === 1 ? '' : 'es'} apartado{detail!.heldCount === 1 ? '' : 's'} para
            la lista de espera (oferta activa).
          </p>
        )}
        {err && <p className="text-sm text-rose-600">{err}</p>}

        <div className="max-h-72 space-y-1 overflow-y-auto">
          {(detail?.attendees ?? []).length === 0 && <p className="text-sm text-slate-400">Nadie ha reservado todavía.</p>}
          {(detail?.attendees ?? []).map((b) => {
            // #578 — cancelación tardía = badge ámbar propio (como no-show), no el gris de "Cancelada".
            const st = b.status === 'CANCELLED' && b.lateCancelled
              ? { label: 'Cancelación tardía', cls: 'bg-amber-100 text-amber-700' }
              : STATUS_META[b.status];
            return (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-700">
                    {b.customerName}
                    {/* V88 — tipo de espacio elegido (snapshot). */}
                    {b.resourceTypeName && (
                      <span className="ml-2 rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                        {b.resourceTypeName}
                      </span>
                    )}
                    {/* V93 — lugar del mapa (snapshot). */}
                    {b.spotName && (
                      <span className="ml-1 rounded-md bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                        {b.spotName}
                      </span>
                    )}
                    {/* Visibilidad de no-shows (requisito 2026-07-04): reincidentes a la vista. */}
                    {b.noShowCount > 0 && (
                      <span className="ml-2 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                            title={`Este email acumula ${b.noShowCount} no-show(s) históricos`}>
                        {b.noShowCount} no-show{b.noShowCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-[12px] text-slate-400">{b.customerEmail}{b.customerPhone ? ` · ${b.customerPhone}` : ''}</p>
                  {/* V75 — respuestas del formulario custom (snapshot label+valor). */}
                  {(b.customAnswers?.length ?? 0) > 0 && (
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {b.customAnswers!.map((a) => `${a.label}: ${a.value}`).join(' · ')}
                    </p>
                  )}
                </div>
                <span className="flex items-center gap-1.5">
                  {/* Epic Pagos: badge de pago. */}
                  {b.paymentStatus === 'paid' && (
                    <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
                          title={b.paidAmount != null ? `Pagó $${Number(b.paidAmount).toLocaleString('es-MX')}` : 'Pagado'}>
                      Pagado
                    </span>
                  )}
                  {b.paymentStatus === 'pending' && (
                    <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Pago pendiente</span>
                  )}
                  <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                  {canWrite && b.status === 'BOOKED' && !cancelled && (
                    <>
                      <button className={btnGhost} onClick={() => setStatus(b, 'ATTENDED')}>Asistió</button>
                      <button className={btnGhost} onClick={() => setStatus(b, 'NO_SHOW')}>No asistió</button>
                      <button className={btnGhost} onClick={() => setMoving(b)}>Mover</button>
                      <button className={btnGhost} onClick={() => cancelBooking(b)}>Cancelar</button>
                    </>
                  )}
                  {/* Epic Pagos: refund manual del pago de una reserva. */}
                  {canWrite && b.paymentStatus === 'paid' && (
                    <button className={btnGhost} onClick={() => refundBooking(b)}>Reembolsar</button>
                  )}
                  {canWrite && (b.status === 'ATTENDED' || b.status === 'NO_SHOW') && (
                    <button className={btnGhost} onClick={() => setStatus(b, 'BOOKED')}>Deshacer</button>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {canWrite && !cancelled && (
          showAdd
            ? <AddBookingForm slotId={slot.id} resourceTypes={detail?.resourceTypes ?? []}
                              onDone={() => { setShowAdd(false); load(); onChanged(); }} onCancel={() => setShowAdd(false)} />
            : <button className={btnPrimary} onClick={() => setShowAdd(true)} disabled={(detail?.freeCount ?? 1) <= 0}>
                + Agregar reserva
              </button>
        )}

        {/* V89 — sala de espera de la sesión */}
        <WaitlistSection slotId={slot.id} canWrite={canWrite} cancelled={cancelled}
                         refreshKey={detail?.waitlistCount ?? 0}
                         onChanged={() => { load(); onChanged(); }} />
      </div>
      {moving && (
        <MoveBookingModal booking={moving} currentSlot={slot} kind={kind}
                          onClose={() => setMoving(null)}
                          onMoved={() => { setMoving(null); load(); onChanged(); }} />
      )}
      {editing && (
        <EditSlotModal slot={slotView} kind={kind} offerings={offerings} instructors={instructors}
                       bookedCount={detail?.bookedCount ?? slotView.bookedCount}
                       onClose={() => setEditing(false)}
                       onSaved={(updated) => {
                         setEditing(false);
                         setSlotView((prev) => ({
                           ...prev,
                           startsAt: updated.startsAt,
                           durationMinutes: updated.durationMinutes,
                           capacity: updated.capacity,
                           notes: updated.notes,
                           offeringId: updated.offeringId,
                           offeringName: offerings.find((o) => o.id === updated.offeringId)?.name ?? prev.offeringName,
                           offeringColor: offerings.find((o) => o.id === updated.offeringId)?.color ?? prev.offeringColor,
                         }));
                         load(); onChanged();
                       }} />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Editar sesión (gap Fase 1, pedido 2026-07-04): hora/fecha, duración, notas,
// cupo y offering (mismo kind). Las reservas vivas SE MUEVEN con la sesión —
// advertencia explícita al admin; notificar al usuario final = fase posterior.
// ---------------------------------------------------------------------------
function EditSlotModal({ slot, kind, offerings, instructors = [], bookedCount, onClose, onSaved }: {
  slot: Slot; kind: 'CLASS' | 'SLOT'; offerings: Offering[]; instructors?: Instructor[];
  bookedCount: number; onClose: () => void; onSaved: (updated: any) => void;
}) {
  const L = bookingLabels(verticalOfKind(kind));
  const [offeringId, setOfferingId] = useState(slot.offeringId);
  const [startsAt, setStartsAt] = useState(toDatetimeLocal(slot.startsAt));
  const [duration, setDuration] = useState(slot.durationMinutes);
  const [capacity, setCapacity] = useState(slot.capacity);
  const [notes, setNotes] = useState(slot.notes ?? '');
  // V91 — instructor de la sesión ('' = sin instructor; el DTO lo vuelve null).
  const [instructorId, setInstructorId] = useState(slot.instructorId ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const timeChanged = new Date(startsAt).getTime() !== new Date(slot.startsAt).getTime();

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const updated = await api(`/booking/slots/${slot.id}`, {
        method: 'PATCH',
        body: {
          ...(timeChanged && { startsAt: new Date(startsAt).toISOString() }),
          ...(duration !== slot.durationMinutes && { durationMinutes: duration }),
          ...(capacity !== slot.capacity && { capacity }),
          ...(notes !== (slot.notes ?? '') && { notes }),
          ...(offeringId !== slot.offeringId && { offeringId }),
          ...(instructorId !== (slot.instructorId ?? '') && { instructorId }),
        },
      });
      onSaved(updated);
    } catch (e: any) { setErr(errMsg(e, 'No se pudo guardar')); setBusy(false); }
  };

  return (
    <Modal title="Editar sesión" onClose={onClose}>
      <div className="space-y-3">
        {bookedCount > 0 && timeChanged && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
            <b>{bookedCount} reserva{bookedCount === 1 ? '' : 's'} se moverá{bookedCount === 1 ? '' : 'n'} a la nueva fecha/hora.</b>{' '}
            Cada cliente recibirá un correo con el nuevo horario (el .ics actualiza su calendario).
          </p>
        )}
        <label className="block text-[12px] text-slate-500">Clase / actividad (mismo tipo)
          <select className={inputCls} value={offeringId} onChange={(e) => setOfferingId(e.target.value)}>
            {offerings.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        {instructors.length > 0 && (
          <label className="block text-[12px] text-slate-500">{L.providerLabel} de {kind === 'SLOT' ? 'la cita' : 'la sesión'}
            <select className={inputCls} value={instructorId} onChange={(e) => setInstructorId(e.target.value)}>
              <option value="">Sin asignar</option>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        )}
        <label className="block text-[12px] text-slate-500">Fecha y hora
          <input type="datetime-local" className={inputCls} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[12px] text-slate-500">Duración (min)
            <input type="number" min={5} className={inputCls} value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
          </label>
          <label className="text-[12px] text-slate-500">{kind === 'CLASS' ? 'Cupo' : 'Estaciones'}
            <input type="number" min={Math.max(1, bookedCount)} className={inputCls} value={capacity} onChange={(e) => setCapacity(Number(e.target.value))} />
          </label>
        </div>
        {bookedCount > 0 && (
          <p className="text-[11px] text-slate-400">El cupo no puede bajar de {bookedCount} (reservas vivas).</p>
        )}
        <label className="block text-[12px] text-slate-500">Notas
          <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
        </label>
        {err && <p className="text-sm text-rose-600">{err}</p>}
        <div className="flex justify-end">
          <button className={btnPrimary} onClick={save} disabled={busy}>Guardar cambios</button>
        </div>
      </div>
    </Modal>
  );
}

function AddBookingForm({ slotId, resourceTypes = [], onDone, onCancel }: {
  slotId: string; resourceTypes?: SlotTypeOccupancy[]; onDone: () => void; onCancel: () => void;
}) {
  const { contacts } = useContacts();
  const [contactId, setContactId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // V88 — la sesión tipada exige elegir tipo (igual que el canal público).
  const [resourceTypeId, setResourceTypeId] = useState('');
  // Fase 6 follow-up — aula con mapa: el PLANO es el selector (mismo picker
  // que el cliente); elegir un lugar elige también su aula. Sin mapa: chips.
  const [planos, setPlanos] = useState<Record<string, { spots: SlotSpot[]; layout: RoomLayout }>>({});
  const [spotId, setSpotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const typed = resourceTypes.length > 0;
  const mapped = resourceTypes.filter((t) => t.hasMap === true);
  const unmapped = resourceTypes.filter((t) => t.hasMap !== true);
  const selectedType = resourceTypes.find((t) => t.id === resourceTypeId);
  const needsSpot = selectedType?.hasMap === true;
  const freeOf = (t: SlotTypeOccupancy) => Math.max(0, t.capacity - t.booked - (t.held ?? 0));

  // Autofill: elegir un contacto llena Nombre/Email/Teléfono desde su ficha
  // (editable después; al deseleccionar los campos se conservan).
  useEffect(() => {
    if (!contactId) return;
    const c = contacts.find((x) => x.id === contactId);
    if (!c) return;
    setName(c.name ?? '');
    setEmail(c.email ?? '');
    setPhone(c.phone ?? '');
  }, [contactId, contacts]);

  // Planos de TODAS las aulas mapeadas de la sesión (tomado = reserva + hold
  // de oferta V90 — mismo contrato que el picker público).
  useEffect(() => {
    let alive = true;
    setPlanos({}); setSpotId('');
    mapped.forEach((t) => {
      api<{ spots: SlotSpot[]; layout: RoomLayout }>(`/booking/slots/${slotId}/spots?resourceTypeId=${t.id}`)
        .then((r) => { if (alive) setPlanos((p) => ({ ...p, [t.id]: { spots: r.spots, layout: r.layout ?? null } })); })
        .catch(() => undefined);
    });
    return () => { alive = false; };
  }, [slotId, mapped.map((t) => t.id).join(',')]);

  const pickSpot = (roomId: string, id: string) => {
    if (!id) {
      // Des-seleccionar el lugar des-selecciona el aula (el plano ES el selector).
      if (resourceTypeId === roomId) { setSpotId(''); setResourceTypeId(''); }
      return;
    }
    setResourceTypeId(roomId);
    setSpotId(id);
  };
  const pickChip = (id: string) => { setResourceTypeId(id); setSpotId(''); };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api('/booking/bookings', {
        method: 'POST',
        body: {
          slotId,
          ...(contactId && { contactId }),
          ...(name.trim() && { customerName: name.trim() }),
          ...(email.trim() && { customerEmail: email.trim() }),
          ...(phone.trim() && { customerPhone: phone.trim() }),
          ...(typed && resourceTypeId && { resourceTypeId }),
          ...(needsSpot && spotId && { spotId }),
        },
      });
      onDone();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo reservar')); setBusy(false); }
  };

  return (
    <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
      <p className="text-[12px] font-medium text-slate-500">Nueva reserva (contacto o nombre + email)</p>
      <ContactPicker value={contactId} onChange={setContactId} placeholder="Buscar contacto (opcional)…" />
      <div className="grid grid-cols-2 gap-2">
        <input className={inputCls} placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
        <input className={inputCls} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <input className={inputCls} placeholder="Teléfono (opcional)" value={phone} onChange={(e) => setPhone(e.target.value)} />
      {/* Aulas SIN mapa: chips como siempre (V88/V90). */}
      {unmapped.length > 0 && (
        <div>
          <p className="text-[12px] text-slate-500">Aula / espacio</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {unmapped.map((t) => {
              // V90: los lugares apartados (hold) no están disponibles ni para el staff.
              const free = freeOf(t);
              return (
                <button key={t.id} onClick={() => pickChip(t.id)} disabled={free <= 0}
                  title={(t.held ?? 0) > 0 ? `${t.held} apartado(s) para la lista de espera` : undefined}
                  className={`rounded-lg border px-2 py-1 text-[11px] font-medium disabled:opacity-40 ${resourceTypeId === t.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'}`}>
                  {t.name} · libres {free}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Fase 6 follow-up — aulas CON mapa: el PLANO es el selector (mismo
          componente que el picker del cliente; lugar REQUERIDO, D5). Tomado =
          reserva + hold de oferta; agregado lleno deshabilita todo el plano. */}
      {mapped.map((t) => (
        <div key={t.id}>
          <p className="text-[12px] text-slate-500">
            {t.name} — elige el lugar (obligatorio)
            {freeOf(t) <= 0 && <span className="ml-1 font-medium text-amber-700">· sin cupo</span>}
            {(t.held ?? 0) > 0 && <span className="ml-1 text-amber-700">· {t.held} apartado{t.held === 1 ? '' : 's'} para la lista de espera</span>}
          </p>
          <div className="mt-1.5">
            <SpotMapPicker spots={planos[t.id]?.spots ?? []}
                           layout={planos[t.id]?.layout ?? null}
                           value={resourceTypeId === t.id ? spotId : ''}
                           onChange={(id) => pickSpot(t.id, id)}
                           disabledAll={freeOf(t) <= 0} />
          </div>
        </div>
      ))}
      {mapped.length > 0 && (
        <p className="text-[10px] text-slate-400">🔒 = ocupado o apartado para la lista de espera.</p>
      )}
      {err && <p className="text-sm text-rose-600">{err}</p>}
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}>Cancelar</button>
        <button className={btnPrimary} onClick={save}
                disabled={busy || (!contactId && (!name.trim() || !email.trim())) || (typed && !resourceTypeId) || (needsSpot && !spotId)}>
          Reservar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// V89 — sala de espera de la sesión (staff): orden, quién sigue, oferta activa
// con deadline, "Ofrecer ahora" (override) y quitar.
// ---------------------------------------------------------------------------
const WAITLIST_STATUS_META: Record<WaitlistEntry['status'], { label: string; cls: string }> = {
  WAITING:   { label: 'Esperando', cls: 'bg-sky-100 text-sky-700' },
  OFFERED:   { label: 'Ofrecido',  cls: 'bg-amber-100 text-amber-700' },
  CONVERTED: { label: 'Reservó',   cls: 'bg-emerald-100 text-emerald-700' },
  EXPIRED:   { label: 'Expiró',    cls: 'bg-slate-100 text-slate-500' },
  DECLINED:  { label: 'Rechazó',   cls: 'bg-slate-100 text-slate-500' },
  REMOVED:   { label: 'Retirado',  cls: 'bg-slate-100 text-slate-500' },
};

function WaitlistSection({ slotId, canWrite, cancelled, refreshKey, onChanged }: {
  slotId: string; canWrite: boolean; cancelled: boolean; refreshKey: number; onChanged: () => void;
}) {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api<{ slotId: string; entries: WaitlistEntry[] }>(`/booking/slots/${slotId}/waitlist`)
      .then((r) => setEntries(r.entries))
      .catch(() => setEntries([]));
  useEffect(() => { load(); }, [slotId, refreshKey]);

  const active = entries.filter((e) => e.status === 'WAITING' || e.status === 'OFFERED');
  const past = entries.filter((e) => e.status !== 'WAITING' && e.status !== 'OFFERED');
  if (entries.length === 0) return null;

  const offerNow = async (e: WaitlistEntry) => {
    setBusy(true); setErr(null);
    try {
      await api(`/booking/waitlist/${e.id}/offer`, { method: 'POST', body: {} });
      await load(); onChanged();
    } catch (ex: any) { setErr(errMsg(ex, 'No se pudo ofrecer')); }
    finally { setBusy(false); }
  };
  const remove = async (e: WaitlistEntry) => {
    if (!window.confirm(`¿Quitar a ${e.customerName} de la lista de espera?`)) return;
    setBusy(true); setErr(null);
    try {
      await api(`/booking/waitlist/${e.id}`, { method: 'DELETE' });
      await load(); onChanged();
    } catch (ex: any) { setErr(errMsg(ex, 'No se pudo quitar')); }
    finally { setBusy(false); }
  };

  let position = 0;
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <p className="text-[12px] font-medium text-slate-500">
        Lista de espera{active.length > 0 ? ` (${active.length})` : ''}
      </p>
      <div className="mt-1 space-y-1">
        {active.map((e) => {
          position += 1;
          const st = WAITLIST_STATUS_META[e.status];
          return (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-700">
                  <span className="mr-1.5 text-[11px] tabular-nums text-slate-400">#{position}</span>
                  {e.customerName}
                  {e.resourceTypeName && (
                    <span className="ml-2 rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">{e.resourceTypeName}</span>
                  )}
                  {/* V93 — lugar apartado por la oferta activa. */}
                  {e.status === 'OFFERED' && e.spotName && (
                    <span className="ml-1 rounded-md bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
                          title="Lugar apartado mientras dura la oferta">{e.spotName}</span>
                  )}
                </p>
                <p className="truncate text-[12px] text-slate-400">
                  {e.customerEmail}
                  {e.status === 'OFFERED' && e.offerExpiresAt && <> · oferta vence {fmtFull(e.offerExpiresAt)}</>}
                </p>
              </div>
              <span className="flex items-center gap-1.5">
                <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                {canWrite && !cancelled && e.status === 'WAITING' && (
                  <button className={btnGhost} disabled={busy} onClick={() => offerNow(e)}
                          title="Le manda ya el correo con el link para reclamar (aunque el sistema no haya detectado lugar)">
                    Ofrecer ahora
                  </button>
                )}
                {canWrite && (
                  <button className={btnGhost} disabled={busy} onClick={() => remove(e)}>Quitar</button>
                )}
              </span>
            </div>
          );
        })}
        {past.length > 0 && (
          <p className="pt-1 text-[11px] text-slate-400">
            Historial: {past.slice(0, 6).map((e) => `${e.customerName} (${WAITLIST_STATUS_META[e.status].label.toLowerCase()})`).join(' · ')}
            {past.length > 6 ? ` · +${past.length - 6}` : ''}
          </p>
        )}
      </div>
      {err && <p className="mt-1 text-sm text-rose-600">{err}</p>}
    </div>
  );
}

function MoveBookingModal({ booking, currentSlot, kind, onClose, onMoved }: {
  booking: Attendee; currentSlot: Slot; kind: 'CLASS' | 'SLOT'; onClose: () => void; onMoved: () => void;
}) {
  const [options, setOptions] = useState<Slot[]>([]);
  const [target, setTarget] = useState('');
  // V93 — destino con aula mapeada: el staff re-asigna lugar (obligatorio).
  // V94 (condición #3): con el PLANO compartido, no dropdown.
  const [spots, setSpots] = useState<SlotSpot[]>([]);
  const [layout, setLayout] = useState<RoomLayout>(null);
  const [spotId, setSpotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const targetSlot = options.find((s) => s.id === target);
  const needsSpot =
    !!booking.resourceTypeId &&
    targetSlot?.resourceTypes?.find((t) => t.id === booking.resourceTypeId)?.hasMap === true;

  useEffect(() => {
    const from = new Date().toISOString();
    const to = new Date(Date.now() + 8 * 7 * DAY_MS).toISOString();
    api<Slot[]>(`/booking/slots?from=${from}&to=${to}&kind=${kind}`)
      .then((rows) => setOptions(rows.filter((s) => s.id !== currentSlot.id && s.status === 'OPEN' && occupiedOf(s) < s.capacity)))
      .catch((e: any) => setErr(errMsg(e, 'No se pudieron cargar los horarios')));
  }, []);

  useEffect(() => {
    setSpotId(''); setSpots([]); setLayout(null);
    if (!target || !needsSpot || !booking.resourceTypeId) return;
    api<{ spots: SlotSpot[]; layout: RoomLayout }>(`/booking/slots/${target}/spots?resourceTypeId=${booking.resourceTypeId}`)
      .then((r) => {
        setSpots(r.spots);
        setLayout(r.layout ?? null);
        // Pre-selección: el MISMO lugar si sigue libre en el destino.
        const same = r.spots.find((s) => !s.taken && s.label === booking.spotName);
        if (same) setSpotId(same.id);
      })
      .catch(() => setSpots([]));
  }, [target, needsSpot, booking.resourceTypeId]);

  const move = async () => {
    if (!target) return;
    setBusy(true); setErr(null);
    try {
      await api(`/booking/bookings/${booking.id}/reschedule`, {
        method: 'PATCH',
        body: { newSlotId: target, ...(needsSpot && spotId && { spotId }) },
      });
      onMoved();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo mover')); setBusy(false); }
  };

  return (
    <Modal title={`Mover a ${booking.customerName}`} onClose={onClose}>
      <div className="space-y-3">
        <select className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">Elige el nuevo horario…</option>
          {options.map((s) => (
            <option key={s.id} value={s.id}>
              {s.offeringName} · {fmtFull(s.startsAt)} · libres {s.capacity - occupiedOf(s)}
            </option>
          ))}
        </select>
        {options.length === 0 && !err && <p className="text-sm text-slate-400">No hay horarios futuros con cupo (próximas 8 semanas).</p>}
        {/* V93/V94 — aula con mapa en el destino: re-asignar lugar en el PLANO
            compartido (pre-selecciona el mismo si está libre; condición #3). */}
        {needsSpot && (
          <div>
            <p className="text-[12px] font-medium text-slate-500">
              Lugar en el destino (obligatorio)
              {booking.spotName ? <span className="text-slate-400"> — su lugar actual: {booking.spotName}</span> : null}
            </p>
            <div className="mt-1.5">
              <SpotMapPicker spots={spots} layout={layout} value={spotId} onChange={setSpotId} />
            </div>
            <p className="mt-1 text-[10px] text-slate-400">🔒 = ocupado o apartado para la lista de espera.</p>
          </div>
        )}
        {err && <p className="text-sm text-rose-600">{err}</p>}
        <div className="flex justify-end">
          <button className={btnPrimary} onClick={move} disabled={busy || !target || (needsSpot && !spotId)}>Mover reserva</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// V70 — Configuración > "Reservas en línea" (admin + bookings:manage en API):
// kill-switch del canal, tope de invitados, antelación y link + QR.
// ---------------------------------------------------------------------------
type ChannelSettings = {
  guestPolicy: 'ALLOW' | 'MEMBERS_ONLY';
  guestMaxPerDay: number;
  cancelMinHours: number;
  isPublicEnabled: boolean;
  // V89 — sala de espera.
  waitlistEnabled: boolean;
  waitlistOfferWindowHours: number;
};

export function BookingChannelSection() {
  const [settings, setSettings] = useState<ChannelSettings | null>(null);
  const [linkKey, setLinkKey] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Estado REAL de cobros en línea (Connect): la tarjeta de "Pagos en línea" ya no dice
  // "Próximamente" — reservas SÍ cobra. null = aún cargando / no se pudo leer (se trata
  // como inactivo, que es el mensaje seguro: "conecta tu cuenta").
  const [chargesEnabled, setChargesEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    api<ChannelSettings>('/booking/settings').then(setSettings)
      .catch((e: any) => setErr(errMsg(e, 'No se pudo cargar la configuración')));
    api<{ key: string }>('/booking/public-link').then((r) => setLinkKey(r.key)).catch(() => {});
    api<{ chargesEnabled: boolean }>('/payments/account')
      .then((r) => setChargesEnabled(!!r.chargesEnabled)).catch(() => setChargesEnabled(false));
  }, []);

  const url = linkKey ? `${window.location.origin}/reservar/${linkKey}` : null;

  useEffect(() => {
    if (!url) return;
    QRCode.toDataURL(url, { width: 480, margin: 1 }).then(setQr).catch(() => setQr(null));
  }, [url]);

  const save = async (patch: Partial<ChannelSettings>) => {
    if (!settings) return;
    setBusy(true); setErr(null); setSaved(false);
    try {
      const next = await api<ChannelSettings>('/booking/settings', { method: 'PUT', body: patch });
      setSettings(next); setSaved(true);
    } catch (e: any) { setErr(errMsg(e, 'No se pudo guardar')); }
    finally { setBusy(false); }
  };

  if (!settings) return null;

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Reservas en línea</h3>
        <p className="text-sm text-slate-500">Tus clientes reservan solos vía link o QR — clases (Wellness) y citas (Boutique).</p>
      </div>
      <div className="space-y-4 px-6 py-5 md:px-8">
        <label className="flex items-center justify-between gap-4">
          <span>
            <span className="block text-sm font-medium text-slate-800">Canal público activo</span>
            <span className="block text-[12px] text-slate-400">Apagado, el link y el QR dejan de funcionar de inmediato.</span>
          </span>
          <button disabled={busy} onClick={() => save({ isPublicEnabled: !settings.isPublicEnabled })}
            className={`relative h-6 w-11 rounded-full transition ${settings.isPublicEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
            aria-label="Canal público">
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${settings.isPublicEnabled ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-[12px] text-slate-500">Reservas por día (sin membresía)
            <input type="number" min={0} max={50} className={inputCls} value={settings.guestMaxPerDay}
                   onChange={(e) => setSettings({ ...settings, guestMaxPerDay: Number(e.target.value) })}
                   onBlur={() => save({ guestMaxPerDay: settings.guestMaxPerDay })} />
          </label>
          <label className="text-[12px] text-slate-500">Antelación por defecto para cancelar/mover (horas)
            <input type="number" min={0} max={720} className={inputCls} value={settings.cancelMinHours}
                   onChange={(e) => setSettings({ ...settings, cancelMinHours: Number(e.target.value) })}
                   onBlur={() => save({ cancelMinHours: settings.cancelMinHours })} />
            <span className="mt-1 block text-[10px] text-slate-400">Se usa cuando el tipo de cita no define su propia ventana.</span>
          </label>
        </div>
        <label className="block text-[12px] text-slate-500">Quién puede reservar
          <select className={inputCls} value={settings.guestPolicy}
                  onChange={(e) => save({ guestPolicy: e.target.value as ChannelSettings['guestPolicy'] })}>
            <option value="ALLOW">Cualquiera (miembros según su plan; invitados con tope por día)</option>
            <option value="MEMBERS_ONLY">Solo miembros con membresía vigente y pagada</option>
          </select>
        </label>

        {/* V89 — sala de espera (waitlist) */}
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="block text-sm font-medium text-slate-800">Lista de espera</span>
              <span className="block text-[12px] text-slate-400">
                Clase llena → el cliente entra a la lista; al liberarse un lugar se le ofrece por correo al siguiente.
              </span>
            </span>
            <button disabled={busy} onClick={() => save({ waitlistEnabled: !settings.waitlistEnabled })}
              className={`relative h-6 w-11 rounded-full transition ${settings.waitlistEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
              aria-label="Lista de espera">
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${settings.waitlistEnabled ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </label>
          {settings.waitlistEnabled && (
            <label className="mt-3 block text-[12px] text-slate-500">Horas para reclamar un lugar ofrecido (1–48)
              <input type="number" min={1} max={48} className={inputCls} value={settings.waitlistOfferWindowHours}
                     onChange={(e) => setSettings({ ...settings, waitlistOfferWindowHours: Number(e.target.value) })}
                     onBlur={() => save({ waitlistOfferWindowHours: settings.waitlistOfferWindowHours })} />
            </label>
          )}
        </div>

        {/* Pagos en línea — lee el estado REAL de Connect (charges_enabled). Ya NO es
            "Próximamente": reservas cobra al reservar cuando la cuenta Stripe está activa.
            El detalle (conectar/gestionar) vive en Configuración → Cobros y pagos. */}
        {chargesEnabled === true ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <p className="text-sm font-medium text-emerald-800">
              Pagos en línea
              <span className="ml-2 rounded-md bg-emerald-200 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">Activos</span>
            </p>
            <p className="mt-1 text-[12px] text-emerald-700">
              Tus clientes pueden pagar la reserva con tarjeta al reservar. Configúralos en <span className="font-medium">Cobros y pagos</span>.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-sm font-medium text-slate-700">Pagos en línea</p>
            <p className="mt-1 text-[12px] text-slate-500">
              Conecta tu cuenta de Stripe en <span className="font-medium">Cobros y pagos</span> para cobrar la reserva al momento de reservar. Mientras tanto, tus clientes pagan al llegar.
            </p>
          </div>
        )}

        {saved && <p className="text-[12px] text-emerald-600">Guardado.</p>}
        {err && <p className="text-sm text-rose-600">{err}</p>}

        {url && (
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <p className="text-[12px] font-medium text-slate-500">Tu link de reservas</p>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-600"
                     value={url} onFocus={(e) => e.target.select()} />
              <button className={btnGhost} onClick={() => navigator.clipboard?.writeText(url)}>Copiar</button>
              {/* #1 (smoke 08/09) — mismo par Copiar+Abrir que la tarjeta de tienda. */}
              <a href={url} target="_blank" rel="noreferrer" className={btnGhost}>Abrir</a>
            </div>
            {qr && (
              <div className="mt-3 flex items-center gap-4">
                <img src={qr} alt="QR de reservas" className="h-28 w-28 rounded-lg border border-slate-200 bg-white p-1" />
                <a href={qr} download="reservas-qr.png" className={btnGhost}>Descargar QR</a>
              </div>
            )}
            <p className="mt-2 text-[11px] text-slate-400">Comparte el link o imprime el QR; el cliente reserva sin crear cuenta.</p>
          </div>
        )}

        {/* V75 (Fase 5, Task D) — preguntas configurables del formulario */}
        <CustomFieldsManager />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// V75 — preguntas del formulario de reserva (admin, bookings:manage).
// Las respuestas quedan como snapshot {label, valor} en cada reserva:
// renombrar o borrar una pregunta NO rompe reservas históricas.
// ---------------------------------------------------------------------------
type CustomField = {
  id: string; label: string; fieldType: 'text' | 'select' | 'date' | 'boolean';
  options: string[] | null; isRequired: boolean; sortOrder: number; isActive: boolean;
};
const FIELD_TYPE_LABEL: Record<CustomField['fieldType'], string> = {
  text: 'Texto', select: 'Opciones', date: 'Fecha', boolean: 'Sí / No',
};

function CustomFieldsManager() {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<CustomField['fieldType']>('text');
  const [options, setOptions] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api<CustomField[]>('/booking/custom-fields').then(setFields).catch(() => setFields([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim()) { setErr('Falta la pregunta'); return; }
    const opts = options.split(',').map((s) => s.trim()).filter(Boolean);
    if (fieldType === 'select' && opts.length < 2) { setErr('Un campo de opciones necesita al menos 2 (separadas por coma)'); return; }
    setBusy(true); setErr(null);
    try {
      await api('/booking/custom-fields', {
        method: 'POST',
        body: {
          label: label.trim(), fieldType, isRequired,
          sortOrder: fields.length,
          ...(fieldType === 'select' && { options: opts }),
        },
      });
      setLabel(''); setOptions(''); setIsRequired(false); setFieldType('text');
      await load();
    } catch (e: any) { setErr(errMsg(e, 'No se pudo crear')); }
    finally { setBusy(false); }
  };
  const patch = async (f: CustomField, body: Record<string, unknown>) => {
    try { await api(`/booking/custom-fields/${f.id}`, { method: 'PUT', body }); await load(); }
    catch (e: any) { setErr(errMsg(e, 'No se pudo actualizar')); }
  };
  const remove = async (f: CustomField) => {
    if (!window.confirm(`¿Borrar la pregunta "${f.label}"? Las reservas ya hechas conservan su respuesta.`)) return;
    try { await api(`/booking/custom-fields/${f.id}`, { method: 'DELETE' }); await load(); }
    catch (e: any) { setErr(errMsg(e, 'No se pudo borrar')); }
  };

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
      <p className="text-[12px] font-medium text-slate-500">Preguntas del formulario de reserva</p>
      <p className="mb-2 text-[11px] text-slate-400">
        Nombre, email y teléfono siempre se piden. Agrega tus propias preguntas y marca cuáles son obligatorias
        (ej. "¿Es tu primera vez?", "¿Lesiones?", fecha de nacimiento). Máx 10 activas.
      </p>
      <div className="space-y-1">
        {fields.map((f) => (
          <div key={f.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm ${f.isActive ? '' : 'opacity-50'}`}>
            <span className="min-w-0">
              <span className="block truncate font-medium text-slate-700">
                {f.label}{f.isRequired && <span className="text-rose-500"> *</span>}
              </span>
              <span className="block text-[11px] text-slate-400">
                {FIELD_TYPE_LABEL[f.fieldType]}{f.options?.length ? `: ${f.options.join(', ')}` : ''}
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <button className={btnGhost} onClick={() => patch(f, { isRequired: !f.isRequired })}>
                {f.isRequired ? 'Hacer opcional' : 'Hacer forzosa'}
              </button>
              <button className={btnGhost} onClick={() => patch(f, { isActive: !f.isActive })}>
                {f.isActive ? 'Desactivar' : 'Activar'}
              </button>
              <button className="rounded-lg border border-rose-200 bg-white px-2.5 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50"
                      onClick={() => remove(f)}>Borrar</button>
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
        <input className={inputCls} placeholder='Pregunta (ej. "¿Es tu primera vez?")' value={label}
               onChange={(e) => setLabel(e.target.value)} maxLength={150} />
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[12px] text-slate-500">Tipo
            <select className={inputCls} value={fieldType} onChange={(e) => setFieldType(e.target.value as CustomField['fieldType'])}>
              {Object.entries(FIELD_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="flex items-end gap-2 pb-2 text-[12px] text-slate-600">
            <input type="checkbox" checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />
            Obligatoria
          </label>
        </div>
        {fieldType === 'select' && (
          <input className={inputCls} placeholder="Opciones separadas por coma (ej. Fuerza, Cardio, Flexibilidad)"
                 value={options} onChange={(e) => setOptions(e.target.value)} />
        )}
        {err && <p className="text-sm text-rose-600">{err}</p>}
        <div className="flex justify-end">
          <button className={btnPrimary} onClick={add} disabled={busy}>Agregar pregunta</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal genérico (mismo look & feel que los modales de Boutique/Gym)
// ---------------------------------------------------------------------------
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          <button className="text-slate-400 hover:text-slate-600" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
